import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { message } from 'antd';
import Swal from 'sweetalert2';
import {
  Container, Box, Paper, Typography, Grid, Button, Divider, Stack,
  Card, CardContent, Chip, Table, TableHead, TableBody, TableRow, TableCell,
  IconButton, Tooltip, Menu, MenuItem, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, FormControl, InputLabel, Select, InputAdornment,
  useMediaQuery, Accordion, AccordionSummary, AccordionDetails, Tabs, Tab,
  List, ListItem, ListItemText, ListItemIcon, OutlinedInput, Avatar
} from '@mui/material';
import Collapse from '@mui/material/Collapse';
import ListItemButton from '@mui/material/ListItemButton';
import { useTheme } from '@mui/material/styles';
import FormHelperText from '@mui/material/FormHelperText';

// Icons
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PrintIcon from '@mui/icons-material/Print';
import PersonIcon from '@mui/icons-material/Person';
import InfoIcon from '@mui/icons-material/Info';
import EventNoteIcon from '@mui/icons-material/EventNote';
import PaidIcon from '@mui/icons-material/Paid';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloseIcon from '@mui/icons-material/Close';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import MoneyIcon from '@mui/icons-material/Money';
import { AssuredWorkloadRounded, TextSnippetSharp, ExpandMore, ExpandLess } from '@mui/icons-material';
import LocalPrintshopTwoToneIcon from '@mui/icons-material/LocalPrintshopTwoTone';
import ReceiptIcon from '@mui/icons-material/Receipt';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import EditIcon from '@mui/icons-material/Edit';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import DiscountIcon from '@mui/icons-material/Discount';
import SearchIcon from '@mui/icons-material/Search';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import HttpsIcon from '@mui/icons-material/Https';

import { Spin } from 'antd';

// Components
import generateThermalReceiptESCPOS from '../utils/generateThermalReceiptESCPOS';
import { generatePaymentReceipt48 } from '../utils/generateThermalReceiptESCPOS';
import { generateCreditNotePDF } from '../utils/generateCreditNotePDF';
import { generateDebitNotePDF } from '../utils/generateDebitNotePDF'
import { InvoiceNotFound, InvoiceError } from './InvoiceStatus';
import LoadingInformation from '../../Components/LoadingInformation';

// Formatting helpers
function parseNumber(str) {
  if (!str) return 0;
  return Number(str.toString().replace(/,/g, ''));
}

function formatCurrency(value) {
  if (value === null || value === undefined) return 'RD$0.00';
  let numValue;
  if (typeof value === 'string') {
    numValue = parseFloat(value.replace(/,/g, ''));
  } else if (typeof value === 'number') {
    numValue = value;
  } else {
    return 'RD$0.00';
  }
  return numValue.toLocaleString('es-DO', {
    style: 'currency',
    currency: 'DOP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

function toMySQLDatetime(dateString) {
  const date = new Date(dateString);
  // Ajustar para la zona horaria local para evitar diferencias UTC
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

const safeParseFloat = (value, defaultValue = 0) => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = parseFloat(value.replace(/,/g, ''));
    return isNaN(parsed) ? defaultValue : parsed;
  }
  return defaultValue;
};

// Agrupa servicios y suma todos los pagos hechos a un mismo selectedServiceID, incluyendo pagos al originalSelectedServiceID si aplica
const groupServicesBySelectedServiceID = (invoiceDetails, debitNotes = [], creditNotes = [], step = 1, comboName = '') => {
  const grouped = {};
  let details;

  if (step === 1) {
    details = invoiceDetails.filter(d => d.serviceID !== 24);
  } else {
    details = invoiceDetails.filter(d => d.serviceID === 24);
    if (details.length > 0) {
      details.forEach(detail => {
        detail.serviceName = comboName;
      });
    }
  }

  // Agrupa todos los detalles por selectedServiceID
  details.forEach(detail => {
    const key = detail.selectedServiceID;
    if (!grouped[key]) {
      grouped[key] = {
        ...detail,
        amount: 0, // Inicializa el monto pagado
        pagos: [], // Para depuración
      };
    }
    grouped[key].amount += parseNumber(detail.amount) || 0;
    grouped[key].pagos.push(parseNumber(detail.amount) || 0);
  });

  // Ahora calcula pendiente, estado, historial, etc.
  return Object.values(grouped).map(detail => {
    let pagadoOriginal = 0;
    let historial = '';
    let creditNote = null;
    let debitNote = null;
    let creditNoteAmount = 0;
    let debitNoteAmount = 0;

    // Buscar si este servicio está afectado por una nota de débito
    if (debitNotes && debitNotes.length > 0) {
      debitNotes.forEach(note => {
        note.details.forEach(d => {
          if (d.selectedServiceID === detail.selectedServiceID) {
            debitNote = d;
            debitNoteAmount = parseNumber(d.amount) || 0;
          }
          // Si es un nuevo servicio creado por nota de débito
          if (d.newServiceID === detail.selectedServiceID) {
            debitNote = d;
          }
        });
      });
    }

    // Buscar si este servicio está afectado por una nota de crédito
    if (creditNotes && creditNotes.length > 0) {
      creditNotes.forEach(note => {
        note.details.forEach(d => {
          if (d.selectedServiceID === detail.selectedServiceID) {
            creditNote = d;
            creditNoteAmount = parseNumber(d.amount) || 0;
          }

          // Si es un nuevo servicio creado por nota de crédito
          if (d.newServiceID === detail.selectedServiceID) {
            creditNote = d;
          }
        });
      });
    }

    // Historial para notas de crédito
    if (creditNote) {
      historial = `Este servicio fue modificado por una nota de crédito (${creditNote.concept}). Se acreditó RD$${formatCurrency(creditNote.amount)}.`;
    }

    // Historial para notas de débito
    if (debitNote) {
      if (debitNote.newServiceID && debitNote.newServiceName) {
        historial = `Este servicio fue reemplazado por "${debitNote.newServiceName}" mediante nota de débito (${debitNote.concept}).`;
      } else if (debitNote.concept) {
        historial = `Este servicio fue modificado por nota de débito (${debitNote.concept}). Se debitó RD$${formatCurrency(debitNote.amount)}.`;
      }
    }

    // Sumar pagos hechos al servicio original si es una corrección
    if (detail.correctionType === 'debit' && detail.originalSelectedServiceID) {
      pagadoOriginal = invoiceDetails
        .filter(s => s.selectedServiceID === detail.originalSelectedServiceID)
        .reduce((sum, s) => sum + parseNumber(s.amount), 0);

      const servicioOriginal = invoiceDetails.find(s => s.selectedServiceID === detail.originalSelectedServiceID);
      historial = servicioOriginal
        ? `Reemplaza a "${servicioOriginal.serviceName}" (pagado RD$${pagadoOriginal})`
        : historial;
    }
    if (detail.correctionType === 'debit' && !detail.originalSelectedServiceID) {
      const nuevoServicio = invoiceDetails.find(s => s.originalSelectedServiceID === detail.selectedServiceID);
      if (nuevoServicio) {
        historial = 'Servicio inactivo por corrección. Nuevo servicio agregado: ' + (nuevoServicio?.serviceName || '');
      }
      else {
        historial = 'Servicio modificado por corrección de precio o cantidad.';
      }
    }
    if (detail.correctionType === 'credit' && !creditNote) {
      const nuevoServicio = invoiceDetails.find(s => s.originalSelectedServiceID === detail.selectedServiceID);
      if (nuevoServicio) {
        historial = 'Servicio inactivo por nota de crédito. Nuevo servicio agregado: ' + (nuevoServicio?.serviceName || '');
      }
      else {
        historial = 'Servicio modificado por nota de crédito. Corrección de precio o cantidad aplicada.';
      }
    }

    // Calcular el total del servicio
    const totalServicio = parseNumber(detail.servicePrice) * parseNumber(detail.serviceQty) * (1 - (parseFloat(detail.discountApplied) || 0) / 100);

    // El pagado total debe incluir pagos al original si es corrección
    const pagadoTotal = (detail.correctionType === 'debit' && detail.originalSelectedServiceID)
      ? pagadoOriginal
      : parseFloat(detail.amount) || 0;

    // El pendiente también debe considerar el pagado total
    const pendiente = (detail.correctionType === 'debit' && detail.originalSelectedServiceID)
      ? Math.max(totalServicio - pagadoOriginal, 0)
      : Math.max(totalServicio - (parseFloat(detail.amount) || 0), 0);

    // Estado visual
    let estado = 'pendiente';
    if (detail.paymentStatus === 'pagado') estado = 'pagado';
    else if (pendiente === 0) estado = 'pagado';

    // Información amigable para el usuario
    let infoUsuario = '';
    if (creditNote) {
      infoUsuario = `Se aplicó una nota de crédito: ${creditNote.concept}. Monto acreditado: ${formatCurrency(creditNote.amount)}.`;
    } else if (debitNote) {
      infoUsuario = `Se aplicó una nota de débito: ${debitNote.concept}. Monto debitado: ${formatCurrency(debitNote.amount)}.`;
      if (debitNote.newServiceName) {
        infoUsuario += ` Nuevo servicio: ${debitNote.newServiceName}.`;
      }
    } else if (historial) {
      infoUsuario = historial;
    }

    return {
      ...detail,
      pendiente,
      pagado: pagadoTotal,
      estado,
      historial,
      totalServicio,
      infoUsuario,
      creditNoteAmount,
      debitNoteAmount
    };
  });
};

function getSQLDateTimeNow() {
  const now = new Date();
  const pad = n => n.toString().padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
}

function calculateTotalPaidWithCredit(details, totalPaid) {
  let totalPaidWithCreditAmount = totalPaid;
  for (const detail of details) {
    if (detail.pendiente === 0) {
      totalPaidWithCreditAmount -= detail.creditNoteAmount;
    }
  }
  return totalPaidWithCreditAmount;
}

function calculateTotalPendingWithCredit(details, totalPending) {
  let totalPendingWithCreditAmount = totalPending;
  for (const detail of details) {
    if (detail.pendiente > 0) {
      totalPendingWithCreditAmount -= detail.creditNoteAmount;
    }
  }
  return totalPendingWithCreditAmount;
}

// Opciones típicas de motivo
const motiveDebitNote = [
  "Actualización de precio",
  "Cambio de servicio",
  "Ajuste por error",
  "Servicio adicional",
  "Otro (especificar)"
];

// Opciones típicas de motivo para nota de crédito
const motiveCreditNote = [
  "Ajuste por error",
  "Cambio de servicio",
  "Devolución de producto",
  "Descuento aplicado",
  "Servicio no prestado",
  "Ajuste de precio",
  "Otro (especificar)"
];

// Main component
function ViewInvoice() {
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messageApi, contextHolder] = message.useMessage();
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [editInvoiceDialogOpen, setEditInvoiceDialogOpen] = useState(false);

  // --- Start: Payment Dialog State Refactor ---
  const [selectedServicesForPayment, setSelectedServicesForPayment] = useState([]);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentServiceAmounts, setPaymentServiceAmounts] = useState({}); // { selectedServiceID: numericAmount }
  const [paymentServiceInputValues, setPaymentServiceInputValues] = useState({}); // { selectedServiceID: "raw string" }
  const [showPerServicePaymentInputs, setShowPerServicePaymentInputs] = useState(false);
  const [banksOptions, setBanksOptions] = useState([]);
  const [reference, setReference] = useState('');
  const [bank, setBank] = useState('');
  const [notes, setNotes] = useState('');
  // --- End: Payment Dialog State Refactor ---

  const [paymentMethod, setPaymentMethod] = useState('efectivo');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentReceipt, setPaymentReceipt] = useState(null);
  const [printerConfig, setPrinterConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [expandedReceipt, setExpandedReceipt] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [anchorElDesktop, setAnchorElDesktop] = useState(null);
  const [anchorElMobile, setAnchorElMobile] = useState(null);
  const [anchorElEdit, setAnchorElEdit] = useState(null);
  const [openEditPrice, setOpenEditPrice] = useState(false);
  const [changeServiceDialogOpen, setChangeServiceDialogOpen] = useState(false);
  const [serviceSearch, setServiceSearch] = useState('');
  const [filteredServices, setFilteredServices] = useState([]);
  const [servicesToChange, setServicesToChange] = useState([]);
  const [newPrices, setNewPrices] = useState([]);
  const [newQtys, setNewQtys] = useState([]);
  const [newDiscounts, setNewDiscounts] = useState([]);
  const [selectedNewServices, setSelectedNewServices] = useState([]);
  const [servicesList, setServicesList] = useState([]);
  const [loadingServices, setLoadingServices] = useState(true);
  const [debitNoteDialogOpen, setDebitNoteDialogOpen] = useState(false);
  const [debitNoteReasons, setDebitNoteReasons] = useState([]); // Array de motivos por servicio
  const [currentServiceIndex, setCurrentServiceIndex] = useState(null);
  const [editServiceIndex, setEditServiceIndex] = useState(null);
  const [actualNoteType, setActualNoteType] = useState(null);
  const [increasePriceDialogOpen, setIncreasePriceDialogOpen] = useState(false);
  const [increasePriceValue, setIncreasePriceValue] = useState('');
  const [increasePriceError, setIncreasePriceError] = useState('');
  const [creditNoteDialogOpen, setCreditNoteDialogOpen] = useState(false);
  const [decreasePriceDialogOpen, setDecreasePriceDialogOpen] = useState(false);
  const [decreasePriceValue, setDecreasePriceValue] = useState('');
  const [decreasePriceError, setDecreasePriceError] = useState('');
  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
  const [discountValue, setDiscountValue] = useState('');
  const [discountError, setDiscountError] = useState('');
  const [notFound, setNotFound] = useState(false);
  const [serverError, setServerError] = useState(false);
  const [permissionError, setPermissionError] = useState(false);

  const [extensionInstalled, setExtensionInstalled] = useState(false);
  const [extensionChecked, setExtensionChecked] = useState(false);

  useEffect(() => {
    const checkExtension = async () => {
      if (typeof chrome === 'undefined' || !chrome.runtime || !chrome.runtime.sendMessage) {
        setExtensionInstalled(false);
        setExtensionChecked(true);
        messageApi.error('La extensión no está instalada o no se puede acceder.');
        return;
      }

      // Obtener ID dinámico de localStorage
      const extensionId = localStorage.getItem('thermalPrinterExtensionId');
      console.log('Extension ID from localStorage en useEffect:', extensionId);

      if (!extensionId) {
        setExtensionInstalled(false);
        setExtensionChecked(true);
        messageApi.error('No se encontró el ID de la extensión en localStorage. Asegúrate de que la extensión esté instalada.');
        return;
      }

      try {
        const response = await new Promise((resolve, reject) => {
          chrome.runtime.sendMessage(
            extensionId, // Usar ID dinámico
            { action: 'ping' },
            (response) => {
              if (chrome.runtime.lastError) {
                messageApi.error('Error al verificar la extensión: ' + chrome.runtime.lastError.message);
                reject(new Error(chrome.runtime.lastError.message));
              } else {
                messageApi.success('La extensión está instalada y accesible.');
                resolve(response);
              }
            }
          );
        });

        setExtensionInstalled(response && response.success);
      } catch (error) {
        console.error('Error verificando extensión:', error);
        messageApi.error('Error al verificar la extensión: ' + error.message);
        setExtensionInstalled(false);
      } finally {
        setExtensionChecked(true);
      }
    };

    checkExtension();
  }, []);

  const isExtensionInstalled = () => {
    return extensionChecked && extensionInstalled;
  };

  useEffect(() => {
    isExtensionInstalled();
  }, [extensionChecked, extensionInstalled]);

  // Utilidades para responsividad
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const openMobile = Boolean(anchorElMobile);
  const openDesktop = Boolean(anchorElDesktop);
  const { invoiceID } = useParams();
  const componentRef = useRef();
  const navigate = useNavigate();

  // =================================================================
  // Api calls and data fetching
  // =================================================================

  const checkPermissions = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setPermissionError(true);
      return messageApi.error({
        content: 'No se encontró el token de autenticación. Por favor, vuelve a iniciar sesión.',
        duration: 5
      }).then(() => {
        navigate('/sign-in');
      });
    }
    setPermissionError(false);
    setLoading(true);
    try {
      const response = await fetch('https://dprincess-training.servehttp.com/users/check-modify-role-permissions', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.status === 403) {
        messageApi.error('No tienes permisos para acceder a esta sección.');
        setPermissionError(true);
        throw new Error('No tienes permisos para acceder a esta sección.');
      }
    } catch (error) {
      messageApi.error(error.message || 'Error al verificar permisos.');
      throw error; // Re-throw to handle it in the calling function
    }
  };

  useEffect(() => {
    checkPermissions();
  }, []);

  // Fetch invoice data by ID
  const fetchInvoiceData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://dprincess-training.servehttp.com/invoices/list-invoice-information/${invoiceID}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Crear la nueva variable con servicios agrupados
        const invoiceComboDetails = groupServicesBySelectedServiceID(data.invoiceAllDetails, data.debitNotes, data.creditNotes, 2, data.comboName);
        const invoiceSelectedServicesDetails = groupServicesBySelectedServiceID(data.invoiceAllDetails, data.debitNotes, data.creditNotes, 1);
        const invoiceDetails = [...invoiceComboDetails, ...invoiceSelectedServicesDetails];
        data.invoiceDetails = invoiceDetails;

        const totalCreditAmount = data.invoiceDetails.reduce((acc, service) => acc + (service.creditNoteAmount || 0), 0);
        const totalDebitAmount = data.invoiceDetails.reduce((acc, service) => acc + (service.debitNoteAmount || 0), 0);

        // Calculate paid and pending amounts
        let paidAmount = 0;
        let reservationPaid = [];

        for (const details of data.invoiceAllDetails) {
          paidAmount += parseNumber(details.amount);
          if (details.serviceID === 24) {
            details.amount = parseNumber(details.amount);
            reservationPaid.push(details);
          }
        }

        data.pendingAmount = parseNumber(data.totalAmount) - paidAmount;
        data.paidAmount = paidAmount;
        data.totalCreditAmount = totalCreditAmount;
        data.totalDebitAmount = totalDebitAmount;
        data.reservationPaid = reservationPaid;
        data.reservationTotalPaid = reservationPaid.reduce((acc, curr) => acc + parseNumber(curr.amount), 0);
        let totalCombo = 0;
        let comboDiscount = 0;
        const reservationService = data.invoiceDetails.find(detail => detail.serviceID === 24);
        if (reservationService) {
          totalCombo = reservationService.totalServicio;
          comboDiscount = reservationService.discountApplied || 0;
        }

        const totalPaidWithCreditAmount = calculateTotalPaidWithCredit(data.invoiceDetails, data.paidAmount);
        const totalPendingWithCreditAmount = calculateTotalPendingWithCredit(data.invoiceDetails, data.pendingAmount);
        data.totalPaidWithCreditAmount = totalPaidWithCreditAmount;
        data.totalPendingWithCreditAmount = totalPendingWithCreditAmount;
        data.totalCombo = totalCombo;
        data.comboDiscount = comboDiscount;
        setInvoice(data);
      } else if (response.status === 404) {
        messageApi.open({
          type: 'error',
          content: 'Factura no encontrada.',
        });
        setNotFound(true);
      } else {
        messageApi.open({
          type: 'error',
          content: 'Error al cargar la factura, por favor intente nuevamente.',
        });
        setServerError(true);
      }
    } catch (error) {
      console.error('Error:', error);
      messageApi.open({
        type: 'error',
        content: 'Error de conexión al cargar la factura.',
      });
      setServerError(true);
    } finally {
      setLoading(false);
    }
  };

  // Fetch payment receipt data
  const fetchPaymentReceiptData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://dprincess-training.servehttp.com/receipts/list-receipts-by-invoice/${invoiceID}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPaymentReceipt(data);
      } else {
        messageApi.open({
          type: 'error',
          content: 'Error al cargar el recibo de pago, por favor intente nuevamente.',
        });
      }
    } catch (error) {
      console.error('Error:', error);
      messageApi.open({
        type: 'error',
        content: 'Error de conexión al cargar el recibo de pago.',
      });
    }
  };

  // Fetch services list
  const fetchServicesList = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://dprincess-training.servehttp.com/services-list`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setServicesList(data);
      } else {
        messageApi.open({
          type: 'error',
          content: 'Error al cargar la lista de servicios, por favor intente nuevamente.',
        });
      }
    } catch (error) {
      console.error('Error:', error);
      messageApi.open({
        type: 'error',
        content: 'Error de conexión al cargar la lista de servicios.',
      });
    }
  };

  // Fetch Bank accounts
  const fetchBankAccounts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://dprincess-training.servehttp.com/billing-services/bankAccounts/list`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setBanksOptions(data);
      } else {
        messageApi.open({
          type: 'error',
          content: 'Error al cargar las cuentas bancarias, por favor intente nuevamente.',
        });
      }
    } catch (error) {
      console.error('Error:', error);
      messageApi.open({
        type: 'error',
        content: 'Error de conexión al cargar las cuentas bancarias.',
      });
    }
  };

  // Post payment data to the server
  const postPaymentData = async () => {
    try {
      const token = localStorage.getItem('token');

      const totalAmountToPay = Object.values(paymentServiceAmounts).reduce((sum, amount) => sum + amount, 0);

      const isConfirmed = await Swal.fire({
        title: 'Confirmar Pago',
        text: `¿Está seguro de registrar el pago de ${formatCurrency(totalAmountToPay)} para los servicios seleccionados?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, registrar pago',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#7c3aed',
        cancelButtonColor: '#d32f2f',
      })
      if (!isConfirmed.isConfirmed) {
        messageApi.info('Pago cancelado por el usuario.');
        return;
      }
      else if (isConfirmed.isConfirmed) {
        setProcessingPayment(true);
        Swal.fire({
          title: 'Procesando Pago',
          text: 'Por favor espere mientras procesamos su pago...',
          allowOutsideClick: false,
          allowEscapeKey: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        // Construct paidServices from paymentServiceAmounts
        const paidServices = Object.keys(paymentServiceAmounts).map(selectedIdStr => {
          const selectedServiceID = Number(selectedIdStr);
          const serviceDetail = invoice.invoiceDetails.find(d => d.selectedServiceID === selectedServiceID);
          const amountToPaid = paymentServiceAmounts[selectedServiceID];

          // Check if payment completes the service
          const isTotalPayment = Math.abs(serviceDetail.pendiente - amountToPaid) < 0.01;

          return {
            ...serviceDetail,
            description: serviceDetail.description || "Pago por servicio: " + serviceDetail.serviceName,
            amountToPaid,
            paymentStatus: isTotalPayment ? "pagado" : "pendiente"
          };
        });

        const newPaymentReceipt = {
          invoiceID: invoiceID,
          transactionDate: getSQLDateTimeNow(),
          totalAmountReceived: totalAmountToPay,
          paymentMethod: paymentMethod,
          bank: bank,
          referenceNumber: reference,
          notes: notes || "Pago de factura " + invoiceID,
          details: paidServices.map(service => ({
            selectedService: service.selectedServiceID,
            concept: 'Pago de servicio: ' + service.serviceName,
            amountPaid: service.amountToPaid,
          }))
        };

        const paymentData = {
          invoiceID: invoiceID,
          paidServices: paidServices,
          paymentMethod: paymentMethod,
          clientID: invoice.clientID,
          flowType: 'ingreso',
          eventID: invoice.eventID || null,
          paymentReceipt: newPaymentReceipt,
          creationDate: toMySQLDatetime(new Date())
        };

        messageApi.loading('Procesando pago...');
        const response = await fetch('https://dprincess-training.servehttp.com/invoices/add-paid-invoice', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(paymentData)
        });

        if (response.ok) {
          const data = await response.json();

          // El pago se registró exitosamente en el servidor
          // Procesar datos de la factura actualizada
          let paidAmount = 0;
          let reservationPaid = [];
          const invoiceDetails = groupServicesBySelectedServiceID(data.invoice.invoiceAllDetails, data.invoice.debitNotes, data.invoice.creditNotes);

          data.invoice.invoiceDetails = invoiceDetails;
          for (const details of data.invoice.invoiceAllDetails) {
            paidAmount += parseNumber(details.amount);
            if (details.serviceID === 24) {
              details.amount = parseNumber(details.amount);
              reservationPaid.push(details);
            }
          }

          data.invoice.pendingAmount = parseNumber(data.totalAmount) - paidAmount;
          data.invoice.paidAmount = paidAmount;
          data.invoice.reservationPaid = reservationPaid;

          // Actualizar el estado de la factura inmediatamente
          setInvoice(data.invoice);

          // Mostrar confirmación de pago exitoso
          Swal.close(); // Cerrar el diálogo de procesamiento
          await Swal.fire({
            icon: 'success',
            title: 'Pago Registrado',
            text: `El pago se registró exitosamente.`,
            confirmButtonText: 'Aceptar'
          });

          // Intentar imprimir solo en desktop y manejar errores de impresión por separado
          if (!isMobile) {
            try {
              Swal.fire({
                title: 'Imprimiendo factura y recibo...',
                text: 'Por favor espere mientras se procesa la impresión.',
                didOpen: () => {
                  Swal.showLoading();
                }
              });
              data.paymentReceipt.isFromPayment = true; // Asegurarse de que el recibo tenga la propiedad correcta
              const escposData = await generatePaymentReceipt48(data.paymentReceipt, invoice);
              const receiptPrintResult = await handleDirectThermalPrinter(escposData);
              const invoiceToPrint = await generateThermalReceiptESCPOS(data.invoice, printerConfig.SuggestedCharsPerLine || 48);
              const invoicePrintResult = await handleDirectThermalPrinter(invoiceToPrint);

              if (!receiptPrintResult?.success || !invoicePrintResult?.success) {
                // Error de impresión, pero pago ya registrado
                Swal.close();
                await Swal.fire({
                  icon: 'warning',
                  title: 'Error de Impresión',
                  text: 'Pago registrado exitosamente, pero hubo un problema al imprimir. Verifique la conexión de la impresora.',
                  confirmButtonText: 'Entendido'
                });
                messageApi.open({
                  type: 'warning',
                  content: 'Pago registrado exitosamente, pero hubo un problema al imprimir. Verifique la conexión de la impresora.',
                  duration: 5,
                });
                // Cerrar el diálogo de pago
                handleClosePaymentDialog();
              } else {
                Swal.close();
                await Swal.fire({
                  icon: 'success',
                  title: 'Impresión Exitosa',
                  text: 'Pago registrado y documentos impresos exitosamente.',
                  confirmButtonText: 'Aceptar'
                });
                messageApi.open({
                  type: 'success',
                  content: 'Pago registrado y documentos impresos exitosamente.',
                  duration: 3,
                });
              }
            } catch (printError) {
              // Error durante la impresión, pero pago ya registrado
              Swal.close();
              console.error('Error durante la impresión:', printError);
              messageApi.open({
                type: 'warning',
                content: 'Pago registrado exitosamente, pero no se pudo imprimir. Verifique que la impresora esté conectada.',
                duration: 5,
              });
              await Swal.fire({
                icon: 'warning',
                title: 'Error de Impresión',
                text: 'Pago registrado exitosamente, pero hubo un problema al imprimir. Verifique la conexión de la impresora.',
                confirmButtonText: 'Entendido'
              });
            }
          } else {
            // En móvil, solo confirmar el pago
            messageApi.open({
              type: 'success',
              content: 'Pago registrado exitosamente.',
              duration: 3,
            });
            Swal.close(); // Cerrar el diálogo de procesamiento
            await Swal.fire({
              icon: 'success',
              title: 'Pago Registrado',
              text: `El pago se registró exitosamente.`,
              confirmButtonText: 'Aceptar'
            });
          }

          // Cerrar diálogo y recargar datos
          handleClosePaymentDialog();
          window.location.reload();

        } else {
          // Error real del servidor
          const errorData = await response.json();
          messageApi.error(`Error al registrar pago: ${errorData.message || 'Intente nuevamente'}`);
          Swal.close(); // Cerrar el diálogo de procesamiento
          await Swal.fire({
            icon: 'error',
            title: 'Error al Registrar Pago',
            text: errorData.message || 'Intente nuevamente',
            confirmButtonText: 'Aceptar'
          });
        }
      }
    } catch (error) {
      Swal.close();
      console.error('Error en registro de pago:', error);
      messageApi.open({
        type: 'error',
        content: 'Error de conexión al registrar pago: ' + (error.message || 'Intente nuevamente.'),
        duration: 5,
        onClose: () => {
          handleClosePaymentDialog();
          fetchInvoiceData(); // Recargar datos de la factura después del pago
          fetchPaymentReceiptData(); // Recargar recibo de pago
        }
      });
    } finally {
      setProcessingPayment(false);
    }
  }

  // Get invoice data
  useEffect(() => {
    fetchInvoiceData();
    fetchPaymentReceiptData();
  }, []);

  useEffect(() => {
    fetchInvoiceData();
  }, [invoiceID]);

  // =================================================================
  // Print functionality for direct thermal printer
  // =================================================================

  // Test connection to C# agent via extension
  const testAgentConnection = async () => {
    console.log('Iniciando testAgentConnection via extensión...');
    const isInstalled = await checkExtensionRealTime();
    if (!isInstalled) {
      console.log('Extensión no instalada para testAgentConnection');
      messageApi.error('La extensión "Proxy Impresora Térmica Local" no está instalada. Instálala para probar la conexión.');
      return;
    }

    try {
      console.log('Enviando proxyRequest para /printers');
      const response = await sendToExtension({
        action: 'proxyRequest',
        path: '/printers',
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.success) {
        const printers = response.data;
        console.log("Printers from C# Agent via extension:", printers);
        messageApi.success("Conexión con agente C# (GET /printers) exitosa!");
      } else {
        console.error("Failed to GET /printers via extension:", response.error);
        messageApi.error("Fallo al conectar con agente C# (GET /printers) via extensión");
      }
    } catch (error) {
      console.error("Error en testAgentConnection via extensión:", error);
      messageApi.error("Error de conexión al intentar GET /printers via extensión");
    }
  };

  // Fetch printer configuration from C# agent via extension
  const fetchAgentPrinterConfiguration = async () => {
    setConfigLoading(true);
    const isInstalled = await checkExtensionRealTime();
    if (!isInstalled) {
      messageApi.error('La extensión "Proxy Impresora Térmica Local" no está instalada. Instálala para obtener configuración de impresora.');
      setConfigLoading(false);
      return;
    }

    try {
      const response = await sendToExtension({
        action: 'proxyRequest',
        path: '/printer-config',
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.success) {
        const config = response.data;
        setPrinterConfig(config); // Guardar toda la configuración
        if (config.IsPrinterSelected) {
          messageApi.success(`Configuración de impresora '${config.SelectedPrinterName}' (${config.SuggestedCharsPerLine} car.) obtenida.`);
        } else {
          messageApi.info(`Agente no tiene impresora predeterminada. Usando ancho ${config.SuggestedCharsPerLine} car.`);
        }
      } else {
        console.error(`Failed to get printer config via extension: ${response.error}`);
        messageApi.error("Fallo al obtener configuración de impresora del agente via extensión.");
        setPrinterConfig({ SelectedPrinterName: null, SuggestedCharsPerLine: 48, IsPrinterSelected: false }); // Fallback
      }
    } catch (error) {
      console.error("Error en fetchAgentPrinterConfiguration via extensión:", error);
      messageApi.error("Error de conexión al contactar agente para configuración de impresora via extensión.");
      setPrinterConfig({ SelectedPrinterName: null, SuggestedCharsPerLine: 48, IsPrinterSelected: false }); // Fallback
    } finally {
      setConfigLoading(false);
    }
  };

  // Función helper para enviar mensajes a la extensión (con ID dinámico o sin ID)
  const sendToExtension = async (message) => {

    if (window.thermalPrinterProxy) {
      // Usar función global inyectada por la extensión
      const response = await window.thermalPrinterProxy.sendMessage(message);

      // Verificar si la respuesta es una instancia de Response (de fetch)
      if (response instanceof Response) {
        try {
          if (response.ok) {
            const data = await response.text();
            return { success: true, data };
          } else {
            const error = await response.text();
            return { success: false, error };
          }
        } catch (e) {
          console.error('Error al leer el body de la Response:', e);
          return { success: false, error: 'Error al procesar la respuesta de la extensión: ' + e.message };
        }
      } else {
        // Asumir que es un objeto con { success, data, error }
        return response;
      }
    } else {
      // Obtener ID dinámico de localStorage
      const extensionId = localStorage.getItem('thermalPrinterExtensionId');
      console.log('Extension ID from localStorage:', extensionId);

      if (extensionId) {
        console.log('Usando chrome.runtime.sendMessage con ID dinámico:', extensionId);
        return new Promise((resolve, reject) => {
          chrome.runtime.sendMessage(
            extensionId,
            message,
            (response) => {
              if (chrome.runtime.lastError) {
                reject(new Error(chrome.runtime.lastError.message));
              } else {
                resolve(response);
              }
            }
          );
        });
      } else {
        messageApi.error('No se encontró el ID de la extensión en localStorage. Asegúrate de que la extensión esté instalada.');
        return { success: false, error: 'No se encontró el ID de la extensión en localStorage.' };
      }
    }
  };

  // Función para chequear la extensión en tiempo real (actualizada)
  const checkExtensionRealTime = async () => {
    if (window.thermalPrinterProxy) {
      console.log('Función global thermalPrinterProxy disponible');
      return true;
    }
    if (typeof chrome === 'undefined' || !chrome.runtime || !chrome.runtime.sendMessage) {
      console.log('Chrome runtime no disponible');
      return false;
    }

    // Obtener ID dinámico de localStorage
    const extensionId = localStorage.getItem('thermalPrinterExtensionId');
    console.log('Extension ID from localStorage:', extensionId);

    if (!extensionId) {
      console.log('No hay ID en localStorage, extensión no disponible');
      return false;
    }

    try {
      console.log('Enviando ping a extensión con ID dinámico:', extensionId);
      const response = await new Promise((resolve, reject) => {
        chrome.runtime.sendMessage(
          extensionId,
          { action: 'ping' },
          (response) => {
            if (chrome.runtime.lastError) {
              reject(new Error(chrome.runtime.lastError.message));
            } else {
              resolve(response);
            }
          }
        );
      });
      console.log('Respuesta de extensión al ping:', response);
      return response && response.success;
    } catch (error) {
      console.error('Error verificando extensión en tiempo real:', error);
      return false;
    }
  };

  const handleDirectThermalPrint = async () => {
    if (isMobile) return messageApi.warning('La impresión térmica no está disponible en dispositivos móviles.');
    if (!invoice) return messageApi.error('No hay datos de factura para imprimir.');
    console.log('Datos de la factura:', invoice);

    // Chequear la extensión en tiempo real antes de imprimir
    const isInstalled = await checkExtensionRealTime();
    if (!isInstalled) {
      return messageApi.error('La extensión "Proxy Impresora Térmica Local" no está instalada o no responde. Instálala para imprimir.');
    }

    messageApi.loading({ content: 'Enviando recibo térmico a la impresora...', key: 'thermalPrint' });

    const charsPerLine = printerConfig?.SuggestedCharsPerLine || 48;
    const escposData = await generateThermalReceiptESCPOS(invoice, charsPerLine);

    try {
      // Envía mensaje a la extensión
      const response = await sendToExtension({
        action: 'proxyRequest',
        path: '/print',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: {
          data: escposData,
          printerName: printerConfig?.IsPrinterSelected ? printerConfig.SelectedPrinterName : null,
          printType: 'RAW'
        }
      });

      if (response && response.success) {
        messageApi.success({ content: `Recibo térmico enviado: ${response.data}`, key: 'thermalPrint', duration: 3 });
        return { success: true, message: `Documento enviado: ${response.data}` };
      } else {
        const errorMsg = response && response.error ? response.error : 'Respuesta inválida de la extensión';
        throw new Error(errorMsg);
      }
    } catch (error) {
      console.error('Error al enviar el recibo térmico:', error);
      messageApi.error({ content: `Error de conexión: ${error.message}`, key: 'thermalPrint', duration: 5 });
      return { success: false, message: error.message };
    }
  };

  const handleDirectThermalPrinter = async (escposData) => {

    // Chequear la extensión en tiempo real antes de imprimir
    const isInstalled = await checkExtensionRealTime();
    if (!isInstalled) {
      return { success: false, message: 'La extensión "Proxy Impresora Térmica Local" no está instalada. Instálala para imprimir.' };
    }

    try {
      const response = await sendToExtension({
        action: 'proxyRequest',
        path: '/print',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: {
          data: escposData,
          printerName: printerConfig?.IsPrinterSelected ? printerConfig.SelectedPrinterName : null,
          printType: 'RAW'
        }
      });

      if (response && response.success) {
        console.log('Respuesta exitosa de la extensión:', response.data);
        return { success: true, message: `Documento enviado a impresora: ${response.data}` };
      } else {
        const errorMsg = response && response.error ? response.error : 'Respuesta inválida de la extensión';
        console.error('Error en respuesta de la extensión:', errorMsg);
        return { success: false, message: `Error de impresión: ${errorMsg}` };
      }
    } catch (error) {
      console.error('Error al enviar el recibo térmico via extensión:', error);
      return { success: false, message: 'Error de conexión con la impresora. Verifique que esté conectada y el agente en ejecución.' };
    }
  };

  // Print functionality for A4 Invoice
  const handlePrintA4 = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `Factura-${invoice?.invoiceNumber || 'Sin-Numero'}`,
    onBeforePrint: () => messageApi.info('Preparando impresión de factura A4...'),
    onAfterPrint: () => messageApi.success('Factura A4 enviada a impresión'),
    onPrintError: (error) => {
      console.error("A4 Print Error:", error); // Log the specific error
      messageApi.error('Error al imprimir factura A4. Intente nuevamente.');
    }
  });

  // =================================================================
  // Handlers for UI interactions
  // =================================================================

  // Increase price dialog handlers ----------------------------------------------------------------------------------//
  // Abrir diálogo de aumento de precio
  const handleOpenIncreasePriceDialog = () => {
    setIncreasePriceValue('');
    setIncreasePriceError('');
    setActualNoteType('Crear nota de débito');
    setIncreasePriceDialogOpen(true);
    setEditInvoiceDialogOpen(false);
  };

  // Cerrar diálogo de aumento de precio
  const handleCloseIncreasePriceDialog = () => {
    setIncreasePriceDialogOpen(false);
    setIncreasePriceValue('');
    setIncreasePriceError('');
    setActualNoteType(null);
    if (currentServiceIndex !== null) {
      handleRemoveServiceFromCreditNote(currentServiceIndex);
    }
    setCurrentServiceIndex(null);
    setEditInvoiceDialogOpen(true);
  };

  // Aplicar aumento de precio
  const handleApplyIncreasePrice = () => {
    const currentService = servicesToChange[currentServiceIndex];
    const currentPrice = parseNumber(currentService?.servicePrice);
    const newPrice = parseFloat(increasePriceValue);

    if (isNaN(newPrice) || newPrice <= currentPrice) {
      setIncreasePriceError('El nuevo precio debe ser mayor al actual.');
      return;
    }

    // Actualiza todos los arrays igual que en el cambio de servicio
    handleUpdateNewPrice(currentServiceIndex, newPrice);
    handleUpdateNewQty(currentServiceIndex, currentService.serviceQty);
    handleUpdateNewDiscount(currentServiceIndex, currentService.discountApplied || 0);

    // Selecciona el mismo servicio pero con el nuevo precio
    handleUpdateSelectedNewService(currentServiceIndex, {
      ...currentService,
      servicePrice: newPrice
    });

    setActualNoteType('Crear nota de débito');
    setIncreasePriceDialogOpen(false);
    setChangeServiceDialogOpen(false);
    setDebitNoteDialogOpen(true);
  };

  // --- Start: Payment Dialog Logic Refactor ---
  const handleOpenPaymentDialog = () => {
    if (invoice.paymentStatus === 'pagado') {
      messageApi.info('Esta factura ya está pagada. No se pueden registrar más pagos.');
      return;
    }
    setSelectedServicesForPayment([]);
    setPaymentServiceAmounts({});
    setPaymentServiceInputValues({});
    setPaymentAmount(0);
    setPaymentMethod('efectivo');
    setShowPerServicePaymentInputs(false);
    setPaymentDialogOpen(true);
  };

  const handleClosePaymentDialog = () => {
    setPaymentDialogOpen(false);
  };

  const handleMultiServiceSelection = (event) => {
    const {
      target: { value },
    } = event;
    const selectedIds = typeof value === 'string' ? value.split(',') : value;

    setSelectedServicesForPayment(selectedIds);

    // Update amounts based on selection
    const newServiceAmounts = {};
    const newServiceInputValues = {};
    selectedIds.forEach(id => {
      const service = invoice.invoiceDetails.find(d => d.selectedServiceID === id);
      if (service) {
        // If already exists, keep it, otherwise add the full pending amount
        if (paymentServiceAmounts[id] !== undefined) {
          newServiceAmounts[id] = paymentServiceAmounts[id];
          newServiceInputValues[id] = paymentServiceInputValues[id];
        } else {
          newServiceAmounts[id] = service.pendiente;
          newServiceInputValues[id] = service.pendiente.toFixed(2);
        }
      }
    });

    setPaymentServiceAmounts(newServiceAmounts);
    setPaymentServiceInputValues(newServiceInputValues);
  };

  const handleServicePaymentAmountChange = (selectedServiceID, rawValue) => {
    setPaymentServiceInputValues(prev => ({ ...prev, [selectedServiceID]: rawValue }));

    if (rawValue === '' || rawValue.endsWith('.')) {
      // Allow temporary invalid states while user is typing
      return;
    }

    const num = parseFloat(rawValue);
    if (isNaN(num)) {
      return;
    }

    const service = invoice.invoiceDetails.find(d => d.selectedServiceID === selectedServiceID);
    const maxAmount = service ? service.pendiente : 0;

    const cappedAmount = Math.max(0, Math.min(num, maxAmount));

    setPaymentServiceAmounts(prev => ({
      ...prev,
      [selectedServiceID]: cappedAmount,
    }));

    // If user typed a value greater than the max, update the input visually to the max
    if (num > maxAmount) {
      setPaymentServiceInputValues(prev => ({ ...prev, [selectedServiceID]: maxAmount.toFixed(2) }));
    }
  };

  // Recalculate total payment amount whenever individual amounts change
  useEffect(() => {
    const total = Object.values(paymentServiceAmounts).reduce((sum, amount) => sum + amount, 0);
    setPaymentAmount(total);
  }, [paymentServiceAmounts]);


  const handleSubmitPayment = async () => {
    if (Object.keys(paymentServiceAmounts).length === 0 || paymentAmount <= 0) {
      messageApi.error('Por favor seleccione al menos un servicio y especifique un monto válido.');
      return;
    }

    await postPaymentData();
  };
  // --- End: Payment Dialog Logic Refactor ---

  // Edit invoice dialog handlers ----------------------------------------------------------------------------------//
  // Open edit invoice dialog
  const handleOpenEditInvoiceDialog = () => {
    setPaymentDialogOpen(false);
    setEditInvoiceDialogOpen(true);
  };

  // Close edit invoice dialog
  const handleCloseEditInvoiceDialog = () => {
    setEditInvoiceDialogOpen(false);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleEditPriceClick = () => {
    setOpenEditPrice((prev) => !prev);
  };

  const handleOpenChangeServiceDialog = async (service, idx = null) => {
    setChangeServiceDialogOpen(true);
    setEditInvoiceDialogOpen(false);
    setServiceSearch('');
    setLoadingServices(true);
    await fetchServicesList();
    setLoadingServices(false);
    setFilteredServices(servicesList);

    let index = idx;
    // Si no hay índice, es un nuevo cambio, lo agregamos al final
    if (index === null) {
      index = servicesToChange.length;
      setServicesToChange(prev => {
        const updated = [...prev];
        updated[index] = service;
        return updated;
      });
      setNewPrices(prev => {
        const updated = [...prev];
        updated[index] = service.servicePrice;
        return updated;
      });
      setNewQtys(prev => {
        const updated = [...prev];
        updated[index] = service.serviceQty;
        return updated;
      });
      setNewDiscounts(prev => {
        const updated = [...prev];
        updated[index] = service.discountApplied || 0;
        return updated;
      });
      setSelectedNewServices(prev => {
        const updated = [...prev];
        updated[index] = service;
        return updated;
      });
    } else {
      // Si ya existe, actualiza los arrays en ese índice
      setServicesToChange(prev => {
        const updated = [...prev];
        updated[index] = service;
        return updated;
      });
      setNewPrices(prev => {
        const updated = [...prev];
        updated[index] = service.servicePrice;
        return updated;
      });
      setNewQtys(prev => {
        const updated = [...prev];
        updated[index] = service.serviceQty;
        return updated;
      });
      setNewDiscounts(prev => {
        const updated = [...prev];
        updated[index] = service.discountApplied || 0;
        return updated;
      });
      setSelectedNewServices(prev => {
        const updated = [...prev];
        updated[index] = service;
        return updated;
      });
    }
    setCurrentServiceIndex(index);
  };

  // Cierra el diálogo y limpia los campos temporales
  const handleCloseChangeServiceDialog = () => {
    setChangeServiceDialogOpen(false);
    setCurrentServiceIndex(null);
    setEditInvoiceDialogOpen(true);
    // Limpia los arrays temporales solo del índice actual
    if (currentServiceIndex !== null) {
      handleRemoveServiceFromDebitNote(currentServiceIndex);
    }
  };

  // Buscar servicios
  const handleServiceSearchChange = (e) => {
    const value = e.target.value;
    setServiceSearch(value);
    const filtered = servicesList.filter(s =>
      s.serviceName.toLowerCase().includes(value.toLowerCase()) ||
      (s.serviceType && s.serviceType.toLowerCase().includes(value.toLowerCase()))
    );
    setFilteredServices(filtered);
  };

  // Seleccionar nuevo servicio para el cambio
  const handleSelectNewService = (service) => {
    handleUpdateSelectedNewService(currentServiceIndex, service);
    handleUpdateNewPrice(currentServiceIndex, parseNumber(service.servicePrice));
    handleUpdateNewQty(currentServiceIndex, service.serviceQty || 1);

    handleUpdateNewDiscount(currentServiceIndex, service.discountApplied || 0);
    const discount = service.discountApplied ? Math.min(service.discountApplied, 35) : 0;
    handleUpdateNewDiscount(currentServiceIndex, discount);
  };

  // Cambiar descuento
  const handleUpdateNewDiscount = (index, value) => {
    let discount = parseFloat(value);
    if (isNaN(discount)) discount = 0;
    if (discount > 35) discount = 35;
    if (discount < 0) discount = 0;
    const updated = [...newDiscounts];
    updated[index] = discount;
    setNewDiscounts(updated);
  };

  // Agregar un nuevo servicio a la nota de débito
  const handleAddServiceToDebitNote = () => {
    setServicesToChange(prev => [...prev, null]);
    setNewPrices(prev => [...prev, '']);
    setNewQtys(prev => [...prev, '']);
    setNewDiscounts(prev => [...prev, '']);
    setSelectedNewServices(prev => [...prev, null]);
    setEditInvoiceDialogOpen(true);
  };

  // Eliminar servicio de la nota de débito
  const handleRemoveServiceFromDebitNote = (index) => {
    setServicesToChange(servicesToChange.filter((_, i) => i !== index));
    setNewPrices(newPrices.filter((_, i) => i !== index));
    setNewQtys(newQtys.filter((_, i) => i !== index));
    setNewDiscounts(newDiscounts.filter((_, i) => i !== index));
    setSelectedNewServices(selectedNewServices.filter((_, i) => i !== index));
  };

  const handleUpdateNewPrice = (index, value) => {
    const updated = [...newPrices];
    updated[index] = value;
    setNewPrices(updated);
  };
  const handleUpdateNewQty = (index, value) => {
    const updated = [...newQtys];
    updated[index] = value;
    setNewQtys(updated);
  };
  const handleUpdateSelectedNewService = (index, value) => {
    const updated = [...selectedNewServices];
    updated[index] = value;
    setSelectedNewServices(updated);
  };

  // Calcula los montos para cada servicio, evitando NaN
  const finalOlds = servicesToChange.map((s, i) =>
    s && !isNaN(s.servicePrice) && !isNaN(s.serviceQty)
      ? (parseNumber(s.servicePrice) || parseNumber(s.price)) * (parseNumber(s.serviceQty) || 1) * (1 - (parseNumber(s.discountApplied) || 0) / 100)
      : (parseNumber(s?.price) || 0) * (parseNumber(s?.serviceQty) || 1)
  );
  const finalNews = newPrices.map((price, i) =>
    !isNaN(price) && !isNaN(newQtys[i])
      ? (parseFloat(price) || 0) * (parseFloat(newQtys[i]) || 1) * (1 - (parseFloat(newDiscounts[i]) || 0) / 100)
      : 0
  );

  // Solo muestra el botón si el cambio actual tiene diferencia
  const currentDiff = finalNews[currentServiceIndex] - finalOlds[currentServiceIndex];
  const noteType =
    currentDiff > 0
      ? 'Crear nota de débito'
      : currentDiff < 0
        ? 'Crear nota de crédito'
        : null;

  // Handler to open debit note dialog ----------------------------------------------------------------------------------------//
  // Abrir/cerrar diálogo de nota de débito
  const handleOpenDebitNoteDialog = () => {
    setActualNoteType('Crear nota de débito');
    setChangeServiceDialogOpen(false);
    setDebitNoteDialogOpen(true);
  };

  const handleCloseDebitNoteDialog = () => {
    setDebitNoteDialogOpen(false);
    setServicesToChange([]);
    setNewPrices([]);
    setNewQtys([]);
    setNewDiscounts([]);
    setSelectedNewServices([]);
    setDebitNoteReasons([]);
    setCurrentServiceIndex(null);
    setEditServiceIndex(null);
    setActualNoteType(null);
    setIncreasePriceValue('');
    setIncreasePriceError('');
    setEditInvoiceDialogOpen(true);
  };

  // Handler to open/close credit note dialog ----------------------------------------------------------------------------------------//
  // Abrir nota de crédito
  const handleOpenCreditNoteDialog = () => {
    setActualNoteType('Crear nota de crédito');
    setChangeServiceDialogOpen(false);
    setCreditNoteDialogOpen(true);
  };

  // Cerrar nota de crédito y limpiar datos
  const handleCloseCreditNoteDialog = () => {
    setCreditNoteDialogOpen(false);
    setServicesToChange([]);
    setNewPrices([]);
    setNewQtys([]);
    setNewDiscounts([]);
    setSelectedNewServices([]);
    setDebitNoteReasons([]);
    setCurrentServiceIndex(null);
    setEditServiceIndex(null);
    setActualNoteType(null);
    setIncreasePriceValue('');
    setIncreasePriceError('');
    setEditInvoiceDialogOpen(true);
  };

  // Añadir servicio a la nota de crédito
  const handleAddServiceToCreditNote = () => {
    setServicesToChange(prev => [...prev, null]);
    setNewPrices(prev => [...prev, '']);
    setNewQtys(prev => [...prev, '']);
    setNewDiscounts(prev => [...prev, '']);
    setSelectedNewServices(prev => [...prev, null]);
    setEditInvoiceDialogOpen(true);
  };

  // Eliminar servicio de la nota de crédito
  const handleRemoveServiceFromCreditNote = (index) => {
    setServicesToChange(servicesToChange.filter((_, i) => i !== index));
    setNewPrices(newPrices.filter((_, i) => i !== index));
    setNewQtys(newQtys.filter((_, i) => i !== index));
    setNewDiscounts(newDiscounts.filter((_, i) => i !== index));
    setSelectedNewServices(selectedNewServices.filter((_, i) => i !== index));
  };

  // ---------------------------------------------------------------------------------------------

  // Abrir diálogo de disminuir precio
  const handleOpenDecreasePriceDialog = () => {
    setDecreasePriceValue('');
    setDecreasePriceError('');
    setActualNoteType('Crear nota de crédito');
    setDecreasePriceDialogOpen(true);
    setEditInvoiceDialogOpen(false);
  };

  // Cerrar diálogo de disminuir precio
  const handleCloseDecreasePriceDialog = () => {
    setDecreasePriceDialogOpen(false);
    setDecreasePriceValue('');
    setDecreasePriceError('');
    setActualNoteType(null);
    if (currentServiceIndex !== null) {
      handleRemoveServiceFromCreditNote(currentServiceIndex);
    }
    setCurrentServiceIndex(null);
    setEditInvoiceDialogOpen(true);
  };

  // Aplicar disminución de precio
  const handleApplyDecreasePrice = () => {
    const currentService = servicesToChange[currentServiceIndex];
    const currentPrice = parseNumber(currentService?.servicePrice);
    const newPrice = parseFloat(decreasePriceValue);

    if (isNaN(newPrice) || newPrice >= currentPrice) {
      setDecreasePriceError('El nuevo precio debe ser menor al actual.');
      return;
    }

    // Actualiza todos los arrays igual que en el cambio de servicio
    handleUpdateNewPrice(currentServiceIndex, newPrice);
    handleUpdateNewQty(currentServiceIndex, currentService.serviceQty);
    handleUpdateNewDiscount(currentServiceIndex, currentService.discountApplied || 0);

    // Selecciona el mismo servicio pero con el nuevo precio
    handleUpdateSelectedNewService(currentServiceIndex, {
      ...currentService,
      servicePrice: newPrice
    });

    setActualNoteType('Crear nota de crédito');
    setDecreasePriceDialogOpen(false);
    setChangeServiceDialogOpen(false);
    setCreditNoteDialogOpen(true);
  };

  // Handlers for apply discount dialog ----------------------------------------------------------------------------------------//
  // Abrir diálogo de descuento
  const handleOpenDiscountDialog = () => {
    setDiscountValue('');

    setDiscountError('');
    setActualNoteType('Crear nota de crédito');
    setDiscountDialogOpen(true);
    setEditInvoiceDialogOpen(false);
  };

  // Cerrar diálogo de descuento
  const handleCloseDiscountDialog = () => {
    setDiscountDialogOpen(false);
    setDiscountValue('');
    setDiscountError('');
    setActualNoteType(null);
    if (currentServiceIndex !== null) {
      handleRemoveServiceFromCreditNote(currentServiceIndex);
    }
    setCurrentServiceIndex(null);
    setEditInvoiceDialogOpen(true);
  };

  // Aplicar descuento
  const handleApplyDiscount = () => {
    const currentService = servicesToChange[currentServiceIndex];
    let discount = parseFloat(discountValue);
    if (discount < 0) {
      discount = 0; // Asegura que el descuento no sea negativo
    }
    if (discount > 35) {
      discount = 35; // Asegura que el descuento no supere el 35%      
    }

    handleUpdateNewPrice(currentServiceIndex, parseNumber(currentService.servicePrice));
    handleUpdateNewQty(currentServiceIndex, currentService.serviceQty);
    handleUpdateNewDiscount(currentServiceIndex, discount);

    handleUpdateSelectedNewService(currentServiceIndex, {
      ...currentService,
      discountApplied: discount
    });

    setActualNoteType('Crear nota de crédito');
    setDiscountDialogOpen(false);
    setChangeServiceDialogOpen(false);
    setCreditNoteDialogOpen(true);
  };

  // ----------------------------------------------------------------------------------------------------------------

  useEffect(() => {
    if (!isMobile) {
      testAgentConnection();
    } // Test connection to C# agent on mount
  }, []);

  useEffect(() => {
    setFilteredServices(servicesList);
  }, [servicesList]);

  useEffect(() => {
    if (!isMobile) {
      fetchAgentPrinterConfiguration(); // Fetch printer configuration on mount
    }
  }, [invoiceID]);

  useEffect(() => {
    if (extensionChecked) {
      fetchAgentPrinterConfiguration();
    }
  }, [extensionChecked]);

  useEffect(() => {
    setAnchorElMobile(null); // Cierra el menú si cambia la vista
    setAnchorElDesktop(null); // Cierra el menú si cambia la vista
  }, [isMobile]);

  useEffect(() => {
    fetchBankAccounts();
  }, []);

  // Renderiza estado de carga
  if (loading) return <LoadingInformation />;

  if (permissionError) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        {contextHolder}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: 2
        }}>
          <Avatar sx={{ bgcolor: '#f44336', width: 64, height: 64 }}>
            {/* Icono de candado para indicar acceso restringido */}
            <HttpsIcon sx={{ fontSize: 40 }} />
          </Avatar>
          <Typography variant="h5" sx={{ color: '#f44336', fontWeight: 700 }}>
            No tienes permiso para acceder a esta sección
          </Typography>
          <Typography variant="body1" sx={{ color: '#666', textAlign: 'center' }}>
            Esta información solo está disponible para usuarios con permisos de administrador.
          </Typography>
        </Box>
      </Container>
    );
  };

  if (notFound) {
    return <InvoiceNotFound />;
  }

  if (serverError) {
    return <InvoiceError />;
  }

  // Vista para dispositivos móviles
  const handleMenuClickMobile = (event) => setAnchorElMobile(event.currentTarget);
  const handleMenuCloseMobile = () => setAnchorElMobile(null);

  // Vista para escritorio
  const handleMenuClickDesktop = (event) => setAnchorElDesktop(event.currentTarget);
  const handleMenuCloseDesktop = () => setAnchorElDesktop(null);

  const filteredServicesToChange = servicesToChange
    .map((service, idx) => ({
      service,
      newService: selectedNewServices[idx],
      oldPrice: parseNumber(finalOlds[idx]),
      newPrice: parseNumber(finalNews[idx]),
      idx
    }))
    .filter(item => item.service && item.newService);

  const fetchWithTimeout = (url, options, timeout = 30000) => {
    return Promise.race([
      fetch(url, options),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('El servidor tardó demasiado en responder.')), timeout)
      )
    ]);
  };

  // Handler para enviar la nota de débito al backend
  const handleSubmitDebitNote = async () => {
    // Filtra servicios válidos (no nulos)
    const validIndexes = servicesToChange
      .map((s, i) => s && selectedNewServices[i] ? i : null)
      .filter(i => i !== null);

    if (validIndexes.length === 0) {
      messageApi.error('Debe agregar al menos un servicio válido para crear una nota de débito.');
      return;
    }

    // Verifica que todos los motivos estén seleccionados
    const motivosCompletos = validIndexes.every(i =>
      debitNoteReasons[i] && debitNoteReasons[i].option && debitNoteReasons[i].option !== ""
    );
    if (!motivosCompletos) {
      messageApi.error('Debe seleccionar un motivo para cada servicio antes de registrar la nota.');
      return;
    }

    // Construye arrays filtrados
    const filteredServicesToChange = validIndexes.map(i => servicesToChange[i]);
    const filteredNewPrices = validIndexes.map(i => newPrices[i]);
    const filteredNewQtys = validIndexes.map(i => newQtys[i]);
    const filteredNewDiscounts = validIndexes.map(i => newDiscounts[i]);
    const filteredSelectedNewServices = validIndexes.map(i => selectedNewServices[i]);
    const filteredReasons = validIndexes.map(i => debitNoteReasons[i]);
    const filteredFinalOlds = validIndexes.map(i => finalOlds[i]);
    const filteredFinalNews = validIndexes.map(i => finalNews[i]);

    const selectedNewServicesData = filteredSelectedNewServices.map((service, index) => ({
      ...service,
      servicePrice: filteredNewPrices[index],
      serviceQty: filteredNewQtys[index],
      discountApplied: filteredNewDiscounts[index] || 0,
      clientID: invoice.clientID,
      invoiceID: invoice.invoiceID,
      correctionType: 'debit',
      originalSelectedServiceID: filteredServicesToChange[index].selectedServiceID,
      newService: service.serviceID !== filteredServicesToChange[index].serviceID, // Verifica si es un nuevo servicio 
    }));
    const priceDifferences = filteredFinalNews.map((newPrice, idx) => {
      const oldPrice = filteredFinalOlds[idx];
      if (oldPrice === 0) {
        return (parseNumber(newPrice) || 0) - (parseNumber(filteredServicesToChange[idx].servicePrice) * (parseNumber(filteredServicesToChange[idx].serviceQty) || 1) * (1 - (parseNumber(filteredServicesToChange[idx].discountApplied) || 0) / 100));
      }
      return (parseNumber(newPrice) || 0) - (parseNumber(oldPrice) || 0);
    });
    let amount = priceDifferences.reduce((sum, diff) => sum + diff, 0);
    const debitNote = {
      invoiceID: Number(invoiceID),
      amount: amount,
      reason: 'Corrección de precios en factura ' + invoice.invoiceNumber,
    }
    const debitNoteDetails = filteredServicesToChange.map((service, index) => ({
      selectedServiceID: service.selectedServiceID,
      amount: priceDifferences[index],
      concept: filteredReasons[index].custom !== '' ? filteredReasons[index].custom : filteredReasons[index].option,
      serviceName: service.serviceName,
      serviceQty: service.serviceQty,
      servicePrice: service.servicePrice
    }));

    const debitNoteData = {
      invoiceID: Number(invoiceID),
      originalSelectedServices: filteredServicesToChange,
      selectedNewServices: selectedNewServicesData,
      debitNote: debitNote,
      debitNoteDetails: debitNoteDetails
    };

    const isConfirmed = await Swal.fire({
      title: '¿Registrar nota de débito?',
      text: `Se registrará una nota de débito por un monto de ${debitNote.amount}.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, registrar',
      cancelButtonText: 'Cancelar'
    });

    if (isConfirmed.isConfirmed) {
      // Mostrar un SWAL de carga
      Swal.fire({
        title: 'Registrando nota de débito...',
        allowOutsideClick: false,
        text: 'Por favor, espere mientras se procesa la solicitud.',
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          Swal.close();
          messageApi.error('Token de autenticación no encontrado. Por favor, inicie sesión nuevamente.');
          Swal.fire({
            title: 'Error',
            text: 'Token de autenticación no encontrado. Por favor, inicie sesión nuevamente.',
            icon: 'error',
            confirmButtonText: 'Aceptar'
          });
          throw new Error('Token no encontrado');
        }
        const response = await fetch(`https://dprincess-training.servehttp.com/debit-notes/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(debitNoteData)
        });

        if (!response.ok) {
          const error = await response.json();
          console.error("Error al registrar nota de débito:", response.status, response.statusText);
          console.error("Error details:", error);
          messageApi.error('Error al registrar la nota de débito. Intente nuevamente.');
          Swal.close();
          Swal.fire({
            title: 'Error',
            text: `No se pudo registrar la nota de débito. ${error.error}`,
            icon: 'error',
            confirmButtonText: 'Aceptar'
          });
          return;
        }

        const data = await response.json();
        Swal.close();
        messageApi.success('Nota de débito registrada con éxito.');
        await Swal.fire({
          title: 'Éxito',
          text: `Nota de débito registrada correctamente. Monto: ${data.debitNote.amount}`,
          icon: 'success',
          confirmButtonText: 'Aceptar'
        });
        if (!isMobile) {
          // Generar y mostrar PDF de la nota de débito
          const isPrintConfirmed = await Swal.fire({
            title: 'Generar documento de nota de débito',
            text: '¿Desea generar e imprimir el documento PDF de la nota de débito?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Sí, generar PDF',
            cancelButtonText: 'No, gracias'
          });

          if (isPrintConfirmed.isConfirmed) {
            Swal.fire({
              title: 'Generando PDF...',
              text: 'Por favor, espere mientras se genera el PDF de la nota de débito.',
              allowOutsideClick: false,
              didOpen: () => {
                Swal.showLoading();
              }
            });
            try {
              // Generar el PDF
              const pdf = generateDebitNotePDF(data.debitNote, invoice, data.debitNoteDetails);

              // Mostrar mensaje de éxito
              messageApi.success('PDF de nota de débito generado exitosamente');

              // Abrir el PDF en una nueva ventana para impresión
              const pdfBlob = pdf.output('blob');
              const pdfUrl = URL.createObjectURL(pdfBlob);
              const newWindow = window.open(pdfUrl, '_blank');
              Swal.close();
              if (newWindow) {
                newWindow.onload = () => {
                  // Esperar un momento para que cargue completamente
                  setTimeout(() => {
                    newWindow.print();
                  }, 500);
                };

                // Limpiar URL después de 60 segundos
                setTimeout(() => {
                  URL.revokeObjectURL(pdfUrl);
                }, 60000);

                Swal.hideLoading();
                await Swal.fire({
                  title: 'PDF Generado',
                  text: 'El PDF de la nota de débito ha sido generado exitosamente.',
                  icon: 'success',
                  confirmButtonText: 'Aceptar'
                });
                return window.location.reload();
              } else {
                // Si no se puede abrir ventana nueva, descargar el PDF
                const link = document.createElement('a');
                link.href = pdfUrl;
                link.download = `Nota_Débito_${data.debitNote.debitNoteNumber}.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(pdfUrl);

                messageApi.info('Se ha descargado el PDF de la nota de débito');
                Swal.close();
                await Swal.fire({
                  title: 'PDF Descargado',
                  text: 'El PDF de la nota de débito no pudo ser impreso, pero ha sido descargado exitosamente.',
                  icon: 'info',
                  confirmButtonText: 'Aceptar'
                });
                return window.location.reload();
              }
            } catch (pdfError) {
              console.error('Error generando PDF:', pdfError);
              messageApi.error('Error al generar el PDF de la nota de débito');
              Swal.close();
              await Swal.fire({
                title: 'Error',
                text: 'No se pudo generar el PDF de la nota de débito.',
                icon: 'error',
                confirmButtonText: 'Aceptar'
              });
            }
          } else {
            messageApi.info('Generación de PDF cancelada. Puede generar el documento más tarde desde la sección de notas de débito.');
            await Swal.fire({
              title: 'PDF No Generado ni impreso',
              text: 'La generación del PDF de la nota de débito ha sido cancelada. Puede generarlo más tarde desde la sección de notas de débito.',
              icon: 'info',
              confirmButtonText: 'Aceptar'
            });
            return window.location.reload();
          }
        }
        // Recargar la pagina
        window.location.reload();
      } catch (error) {
        console.error("Error al registrar nota de débito:", error);
        Swal.close();
        messageApi.error('Error al registrar la nota de débito. Intente nuevamente.');
        Swal.fire({
          title: 'Error',
          text: `No se pudo registrar la nota de débito. ${error.message}`,
          icon: 'error',
          confirmButtonText: 'Aceptar'
        });
        return;
      }
    }
    else {
      messageApi.error('Operación cancelada por el usuario.');
      return;
    }
  }

  // Handler para enviar la nota de débito al backend
  const handleSubmitCreditNote = async () => {
    // Filtra servicios válidos (no nulos)
    const validIndexes = servicesToChange
      .map((s, i) => s && selectedNewServices[i] ? i : null)
      .filter(i => i !== null);

    if (validIndexes.length === 0) {
      messageApi.error('Debe agregar al menos un servicio válido para crear una nota de crédito.');
      return;
    }

    // Verifica que todos los motivos estén seleccionados
    const motivosCompletos = validIndexes.every(i =>
      debitNoteReasons[i] && debitNoteReasons[i].option && debitNoteReasons[i].option !== ""
    );
    if (!motivosCompletos) {
      messageApi.error('Debe seleccionar un motivo para cada servicio antes de registrar la nota.');
      return;
    }

    // Construye arrays filtrados
    const filteredServicesToChange = validIndexes.map(i => servicesToChange[i]);
    const filteredNewPrices = validIndexes.map(i => newPrices[i]);
    const filteredNewQtys = validIndexes.map(i => newQtys[i]);
    const filteredNewDiscounts = validIndexes.map(i => newDiscounts[i]);
    const filteredSelectedNewServices = validIndexes.map(i => selectedNewServices[i]);
    const filteredReasons = validIndexes.map(i => debitNoteReasons[i]);
    const filteredFinalOlds = validIndexes.map(i => finalOlds[i]);
    const filteredFinalNews = validIndexes.map(i => finalNews[i]);

    const selectedNewServicesData = filteredSelectedNewServices.map((service, index) => ({
      ...service,
      servicePrice: filteredNewPrices[index],
      serviceQty: filteredNewQtys[index],
      discountApplied: filteredNewDiscounts[index] || 0,
      clientID: invoice.clientID,
      invoiceID: invoice.invoiceID,
      correctionType: 'credit',
      originalSelectedServiceID: filteredServicesToChange[index].selectedServiceID,
      newService: service.serviceID !== filteredServicesToChange[index].serviceID,
    }));

    // Calcula la diferencia de precios (monto a acreditar)
    const priceDifferences = filteredFinalOlds.map((oldPrice, idx) => {
      const newPrice = filteredFinalNews[idx];
      if (oldPrice === 0) {
        return (parseNumber(filteredServicesToChange[idx].servicePrice) * (parseNumber(filteredServicesToChange[idx].serviceQty) || 1) * (1 - (parseNumber(filteredServicesToChange[idx].discountApplied) || 0) / 100)) - (parseNumber(newPrice) || 0);
      }
      return (parseNumber(oldPrice) || 0) - (parseNumber(newPrice) || 0);
    });

    let amount = priceDifferences.reduce((sum, diff) => sum + diff, 0);

    const creditNote = {
      invoiceID: Number(invoiceID),
      amount: amount,
      reason: 'Corrección de precios en factura ' + invoice.invoiceNumber,
    };

    const creditNoteDetails = filteredServicesToChange.map((service, index) => ({
      selectedServiceID: service.selectedServiceID,
      amount: priceDifferences[index],
      concept: filteredReasons[index].custom !== '' ? filteredReasons[index].custom : filteredReasons[index].option,
    }));

    const creditNoteData = {
      invoiceID: Number(invoiceID),
      originalSelectedServices: filteredServicesToChange,
      selectedNewServices: selectedNewServicesData,
      creditNote: creditNote,
      creditNoteDetails: creditNoteDetails
    };

    const isConfirmed = await Swal.fire({
      title: '¿Registrar nota de crédito?',
      text: `Se registrará una nota de crédito por un monto de ${creditNote.amount}.`,
      icon: 'info',
      showCancelButton: true,
      confirmButtonText: 'Sí, registrar',
      cancelButtonText: 'Cancelar'
    });

    if (isConfirmed.isConfirmed) {
      Swal.fire({
        title: 'Registrando nota de crédito...',
        allowOutsideClick: false,
        text: 'Por favor, espere mientras se procesa la solicitud.',
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          Swal.close();
          messageApi.error('Token de autenticación no encontrado. Por favor, inicie sesión nuevamente.');
          Swal.fire({
            title: 'Error',
            text: 'Token de autenticación no encontrado. Por favor, inicie sesión nuevamente.',
            icon: 'error',
            confirmButtonText: 'Aceptar'
          });
          throw new Error('Token no encontrado');
        }
        const response = await fetchWithTimeout(`https://dprincess-training.servehttp.com/credit-notes/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(creditNoteData)
        }, 60000); // Timeout de 60 segundos

        if (!response.ok) {
          const error = await response.json();
          console.error("Error al registrar nota de crédito:", response.status, response.statusText);
          console.error("Error details:", error);
          Swal.close();
          messageApi.error('Error al registrar la nota de crédito. Intente nuevamente.');
          Swal.fire({
            title: 'Error',
            text: `No se pudo registrar la nota de crédito. ${error.error}`,
            icon: 'error',
            confirmButtonText: 'Aceptar'
          });
          return;
        }

        const data = await response.json();
        Swal.close();
        messageApi.success('Nota de crédito registrada con éxito.');
        await Swal.fire({
          title: 'Éxito',
          text: `Nota de crédito registrada correctamente. Monto: ${data.creditNote.amount}`,
          icon: 'success',
          confirmButtonText: 'Aceptar'
        });
        if (!isMobile) {
          // Generar y mostrar PDF de la nota de crédito
          const isPrintConfirmed = await Swal.fire({
            title: 'Generar documento de nota de crédito',
            text: '¿Desea generar e imprimir el documento PDF de la nota de crédito?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Sí, generar PDF',
            cancelButtonText: 'No, gracias'
          });

          if (isPrintConfirmed.isConfirmed) {
            try {
              // Generar el PDF
              const pdf = generateCreditNotePDF(data.creditNote, invoice, data.creditNoteDetails);

              // Mostrar mensaje de éxito
              messageApi.success('PDF de nota de crédito generado exitosamente');

              // Abrir el PDF en una nueva ventana para impresión
              const pdfBlob = pdf.output('blob');
              const pdfUrl = URL.createObjectURL(pdfBlob);
              const newWindow = window.open(pdfUrl, '_blank');

              if (newWindow) {
                newWindow.onload = () => {
                  // Esperar un momento para que cargue completamente
                  setTimeout(() => {
                    newWindow.print();
                  }, 500);
                };

                // Limpiar URL después de 60 segundos
                setTimeout(() => {
                  URL.revokeObjectURL(pdfUrl);
                }, 60000);

                await Swal.fire({
                  title: 'PDF Generado',
                  text: 'El PDF de la nota de crédito ha sido generado exitosamente.',
                  icon: 'success',
                  confirmButtonText: 'Aceptar'
                });
                return window.location.reload();
              } else {
                // Si no se puede abrir ventana nueva, descargar el PDF
                const link = document.createElement('a');
                link.href = pdfUrl;
                link.download = `Nota_Crédito_${data.creditNote.creditNoteNumber}.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(pdfUrl);

                messageApi.info('Se ha descargado el PDF de la nota de crédito');
                await Swal.fire({
                  title: 'PDF Descargado',
                  text: 'El PDF de la nota de crédito no pudo ser impreso, pero ha sido descargado exitosamente.',
                  icon: 'info',
                  confirmButtonText: 'Aceptar'
                });
                return window.location.reload();
              }
            } catch (pdfError) {
              console.error('Error generando PDF:', pdfError);
              messageApi.error('Error al generar el PDF de la nota de crédito');
            }
          } else {
            messageApi.info('Generación de PDF cancelada. Puede generar el documento más tarde desde la sección de notas de crédito.');
            await Swal.fire({
              title: 'PDF No Generado ni impreso',
              text: 'La generación del PDF de la nota de crédito ha sido cancelada. Puede generarlo más tarde desde la sección de notas de crédito.',
              icon: 'info',
              confirmButtonText: 'Aceptar'
            });
            return window.location.reload();
          }
        }
        else {
          // Recargar la página para mostrar los cambios
          window.location.reload();
        }
      } catch (error) {
        if (error.message === 'El servidor tardó demasiado en responder.') {
          messageApi.error('El servidor tardó demasiado en responder. Intente nuevamente.');
          Swal.fire({
            title: 'Error de tiempo de espera',
            text: 'El servidor tardó demasiado en responder. Intente nuevamente.',
            icon: 'error',
            confirmButtonText: 'Aceptar'
          });
        } else {
          messageApi.error('Error al registrar la nota de crédito. Intente nuevamente.');
          Swal.fire({
            title: 'Error',
            text: `No se pudo registrar la nota de crédito. ${error.message}`,
            icon: 'error',
            confirmButtonText: 'Aceptar'
          });
        }
        return;
      }
    } else {
      messageApi.error('Operación cancelada por el usuario.');
      return;
    }
  };

  return (
    <Container sx={{ py: 4 }}>
      {contextHolder}
      {invoice ? (
        isMobile
          ?
          <Box sx={{ width: '100%' }}>
            {/* Encabezado para móvil */}
            <Paper
              elevation={1}
              sx={{
                p: 2,
                mb: 2,
                borderRadius: 2,
                position: 'relative',
                background: 'linear-gradient(145deg, rgba(243,232,255,1) 0%, rgba(252,231,243,1) 100%)',
              }}
            >
              <Box sx={{ position: 'absolute', top: 10, right: 10 }}>
                <IconButton onClick={handleMenuClickMobile} size="small">
                  <MoreVertIcon sx={{ color: '#7c3aed' }} />
                </IconButton>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <ReceiptLongIcon sx={{ color: '#f759ab', fontSize: 36, mr: 1.5 }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#4b0082', lineHeight: 1.2 }}>
                    D'Princess
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#7c3aed' }}>
                    Documento de Facturación
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 1.5 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Factura #{invoice?.invoiceNumber || '0000'}
                </Typography>

                <Chip
                  label={invoice?.paymentStatus?.toUpperCase() || 'PENDIENTE'}
                  size="small"
                  color={invoice?.paymentStatus === "pagado" ? "success" : "warning"}
                  icon={invoice?.paymentStatus === "pagado" ? <CheckCircleIcon /> : <PendingIcon />}
                />
              </Box>

              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#666' }}>
                Fecha: {invoice?.invoiceDate ? new Date(invoice.invoiceDate).toLocaleDateString('es-DO') : 'No registrada'}
              </Typography>
            </Paper>

            {/* Tabs para secciones en móvil */}
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                mb: 2,
                '& .MuiTab-root': {
                  fontSize: '0.8rem',
                  minHeight: '48px'
                },
                '& .Mui-selected': {
                  color: '#7c3aed',
                  fontWeight: 600
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#7c3aed'
                }
              }}
            >
              <Tab icon={<InfoIcon />} label="Información" iconPosition="start" />
              <Tab icon={<PointOfSaleIcon />} label="Servicios" iconPosition="start" />
              <Tab icon={<ReceiptIcon />} label="Pagos" iconPosition="start" />
              <Tab icon={<TrendingUpIcon />} label="Notas de Débito" iconPosition="start" />
              <Tab icon={<DiscountIcon />} label="Notas de Crédito" iconPosition="start" />
            </Tabs>

            {/* Contenido según tab seleccionado */}
            {/* Información de la factura */}
            {tabValue === 0 && (
              <Box>
                {/* Cliente */}
                <Accordion defaultExpanded sx={{ mb: 2, boxShadow: 'none', border: '1px solid rgba(124, 58, 237, 0.1)' }}>
                  <AccordionSummary
                    expandIcon={<ExpandMore sx={{ color: '#7c3aed' }} />}
                    sx={{ backgroundColor: 'rgba(243, 232, 255, 0.3)' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <PersonIcon sx={{ color: '#7c3aed', mr: 1 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#4b0082' }}>
                        CLIENTE
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                      {invoice?.clientName || 'Cliente no registrado'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                      {invoice?.address || 'Dirección no registrada'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                      {invoice?.phoneNumber || 'Teléfono no registrado'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      {invoice?.clientEmail || 'Email no registrado'}
                    </Typography>
                  </AccordionDetails>
                </Accordion>

                {/* Evento */}
                {(invoice?.eventTitle || invoice?.eventLocation) && (
                  <Accordion sx={{ mb: 2, boxShadow: 'none', border: '1px solid rgba(247, 89, 171, 0.2)' }}>
                    <AccordionSummary
                      expandIcon={<ExpandMore sx={{ color: '#f759ab' }} />}
                      sx={{ backgroundColor: 'rgba(247, 89, 171, 0.05)' }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <EventNoteIcon sx={{ color: '#f759ab', mr: 1 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#f759ab' }}>
                          EVENTO
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                        <b>Título:</b> {invoice?.eventTitle || 'No registrado'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                        <b>Lugar:</b> {invoice?.eventLocation || 'No registrado'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                        <b>Inicio:</b> {invoice?.eventDateStart
                          ? new Date(invoice.eventDateStart).toLocaleString('es-DO')
                          : 'No registrada'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#666' }}>
                        <b>Fin:</b> {invoice?.eventDateEnd
                          ? new Date(invoice.eventDateEnd).toLocaleString('es-DO')
                          : 'No registrada'}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Totales */}
                <Paper
                  elevation={2}
                  sx={{
                    p: 2,
                    mb: 2,
                    borderRadius: 2,
                    background: 'linear-gradient(145deg, rgba(124, 58, 237, 0.08) 0%, rgba(139, 92, 246, 0.05) 100%)',
                    border: '1px solid rgba(124, 58, 237, 0.1)'
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#4b0082', mb: 1.5 }}>
                    RESUMEN DE PAGO
                  </Typography>

                  <Grid container spacing={1.5}>
                    <Grid item xs={6}>
                      <Typography variant="body2" sx={{ color: '#666' }}>Subtotal:</Typography>
                    </Grid>
                    <Grid item xs={6} sx={{ textAlign: 'right' }}>
                      <Typography variant="body2" sx={{ fontStyle: (invoice.totalCreditAmount > 0 || invoice.totalDebitAmount > 0) ? 'line-through' : 'normal' }}>{formatCurrency(parseNumber(invoice.totalAmount))}</Typography>
                    </Grid>

                    <Grid item xs={6}>
                      <Typography variant="body2" sx={{ color: '#666' }}>Pagado:</Typography>
                    </Grid>
                    <Grid item xs={6} sx={{ textAlign: 'right' }}>
                      <Typography variant="body2" sx={{ color: '#22c55e', fontWeight: 500 }}>
                        {formatCurrency(parseNumber(invoice?.paidAmount))}
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <Divider sx={{ my: 0.5 }} />
                    </Grid>

                    <Grid item xs={6}>
                      <Typography variant="subtitle2" sx={{ color: '#4b0082', fontWeight: 600 }}>
                        Pendiente:
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sx={{ textAlign: 'right' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#d97706' }}>
                        {formatCurrency(parseNumber(invoice?.pendingAmount))}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>

                {/* Botones de acción */}
                <Stack direction="column" spacing={1.5} sx={{ mb: 2 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<PaidIcon />}
                    onClick={handleOpenPaymentDialog}
                    sx={{
                      bgcolor: '#7c3aed',
                      '&:hover': { bgcolor: '#6d28d9' },
                      py: 1.2
                    }}
                  >
                    Registrar Pago
                  </Button>

                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<PrintIcon />}
                    onClick={handlePrintA4}
                    sx={{
                      borderColor: '#7c3aed',
                      color: '#7c3aed',
                      '&:hover': { borderColor: '#6d28d9', bgcolor: 'rgba(124, 58, 237, 0.04)' },
                      py: 1.2
                    }}
                    disabled={isMobile}
                  >
                    Imprimir Factura A4
                  </Button>

                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<LocalPrintshopTwoToneIcon />}
                    onClick={handleDirectThermalPrint}
                    disabled={configLoading || !printerConfig}
                    sx={{
                      borderColor: '#f759ab',
                      color: '#f759ab',
                      '&:hover': { borderColor: '#e64980', bgcolor: 'rgba(247, 89, 171, 0.04)' },
                      py: 1.2
                    }}
                  >
                    Imprimir Térmica
                  </Button>
                </Stack>
              </Box>
            )}

            {/* Servicios contratados */}
            {tabValue === 1 && (
              <Box>
                {/* Servicios contratados */}
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: '#4b0082',
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <PointOfSaleIcon sx={{ mr: 1, fontSize: 20 }} />
                  SERVICIOS CONTRATADOS
                </Typography>

                {/* Renderiza todos los servicios, incluyendo los modificados por notas */}
                {invoice?.invoiceDetails &&
                  invoice.invoiceDetails.map((detail, index) => (
                    <Paper
                      key={index}
                      elevation={1}
                      sx={{
                        p: 2,
                        mb: 2,
                        borderRadius: 2,
                        border: '1px solid rgba(124, 58, 237, 0.05)',
                        opacity: detail.estado === 'inactivo' ? 0.6 : 1,
                        background: detail.correctionType === 'credit'
                          ? 'linear-gradient(90deg, #e0e7ff 60%, #fdf2f8 100%)'
                          : detail.correctionType === 'debit'
                            ? 'linear-gradient(90deg, #fdf2f8 60%, #f3e8ff 100%)'
                            : 'white'
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {detail.serviceName}
                          </Typography>
                          <Chip
                            label={detail.correctionType === 'credit'
                              ? 'Modificado por nota de crédito'
                              : detail.correctionType === 'debit'
                                ? 'Modificado por nota de débito'
                                : 'Servicio'}
                            size="small"
                            sx={{
                              mt: 0.5,
                              bgcolor: detail.correctionType === 'credit'
                                ? 'rgba(37,99,235,0.08)'
                                : detail.correctionType === 'debit'
                                  ? 'rgba(179,25,210,0.08)'
                                  : 'rgba(247, 89, 171, 0.1)',
                              color: detail.correctionType === 'credit'
                                ? '#2563eb'
                                : detail.correctionType === 'debit'
                                  ? '#b319d2ff'
                                  : '#f759ab',
                              fontSize: '0.7rem'
                            }}
                          />
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {formatCurrency(detail.totalServicio)}
                        </Typography>
                      </Box>

                      <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1 }}>
                        Cantidad: {detail.serviceQty} × {formatCurrency(parseNumber(detail.servicePrice))}
                        {detail.discountApplied > 0 && ` (-${detail.discountApplied}% descuento)`}
                      </Typography>

                      {/* Historial de cambios por nota */}
                      {detail.historial && (
                        <Typography variant="caption" sx={{
                          color: detail.correctionType === 'credit'
                            ? '#2563eb'
                            : detail.correctionType === 'debit'
                              ? '#b319d2ff'
                              : '#d97706',
                          fontWeight: 600,
                          mb: 1,
                          display: 'block'
                        }}>
                          {detail.historial}
                        </Typography>
                      )}

                      <Divider sx={{ my: 1 }} />

                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="caption" sx={{ color: '#666' }}>Pagado:</Typography>
                        </Grid>
                        <Grid item xs={6} sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" sx={{ color: '#22c55e' }}>
                            {formatCurrency(detail.pagado)}
                          </Typography>
                        </Grid>

                        <Grid item xs={6}>
                          <Typography variant="caption" sx={{ color: '#666', fontWeight: 500 }}>Pendiente:</Typography>
                        </Grid>
                        <Grid item xs={6} sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" sx={{ color: '#d97706', fontWeight: 600 }}>
                            {formatCurrency(detail.pendiente)}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  ))
                }
              </Box>
            )}

            {/* Recibos de pago */}
            {tabValue === 2 && (
              <Box>
                {/* Recibos de pago */}
                {paymentReceipt && paymentReceipt.length > 0 ? (
                  <Stack spacing={2}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        color: '#4b0082',
                        mb: 1,
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <ReceiptIcon sx={{ mr: 1, fontSize: 20 }} />
                      RECIBOS DE PAGO
                    </Typography>

                    {paymentReceipt.map((receipt, idx) => (
                      <Accordion
                        key={receipt.receiptDocumentNumber || idx}
                        expanded={expandedReceipt === idx}
                        onChange={() => setExpandedReceipt(expandedReceipt === idx ? null : idx)}
                        sx={{
                          boxShadow: 'none',
                          border: '1px solid #ddd6fe',
                          borderRadius: '8px !important',
                          '&:before': {
                            display: 'none',
                          },
                          '&.Mui-expanded': {
                            margin: 0
                          }
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMore sx={{ color: '#7c3aed' }} />}
                          sx={{
                            backgroundColor: expandedReceipt === idx ? 'rgba(124, 58, 237, 0.08)' : 'white',
                            borderRadius: '8px',
                            '&.Mui-expanded': {
                              borderBottomLeftRadius: 0,
                              borderBottomRightRadius: 0,
                            }
                          }}
                        >
                          <Box sx={{ width: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <ReceiptIcon sx={{ color: '#7c3aed', mr: 1, fontSize: 18 }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#4b0082' }}>
                                  {receipt.receiptDocumentNumber || 'Recibo'}
                                </Typography>
                              </Box>
                              <Chip
                                label={receipt.paymentMethod?.toUpperCase() || 'N/D'}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: '0.7rem',
                                  bgcolor: receipt.paymentMethod === 'efectivo'
                                    ? 'rgba(34, 197, 94, 0.1)'
                                    : 'rgba(59, 130, 246, 0.1)',
                                  color: receipt.paymentMethod === 'efectivo'
                                    ? '#22c55e'
                                    : '#3b82f6'
                                }}
                              />
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="caption" sx={{ color: '#666' }}>
                                {receipt.transactionDate
                                  ? new Date(receipt.transactionDate).toLocaleDateString('es-DO')
                                  : ''}
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#22c55e' }}>
                                {formatCurrency(receipt.totalAmountReceived)}
                              </Typography>
                            </Box>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ bgcolor: '#f8fafc', pt: 2 }}>
                          <Typography variant="caption" sx={{ display: 'block', color: '#666', mb: 1 }}>
                            <b>Registrado por:</b> {receipt.employeeName || 'No registrado'}
                          </Typography>

                          {receipt.referenceNumber && (
                            <Typography variant="caption" sx={{ display: 'block', color: '#666', mb: 1 }}>
                              <b>Referencia:</b> {receipt.referenceNumber}
                            </Typography>
                          )}

                          {receipt.notes && (
                            <Typography variant="caption" sx={{ display: 'block', color: '#666', mb: 1 }}>
                              <b>Notas:</b> {receipt.notes}
                            </Typography>
                          )}

                          <Divider sx={{ my: 1.5 }} />

                          <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, color: '#4b0082', mb: 1 }}>
                            Conceptos Pagados:
                          </Typography>

                          {receipt.details && receipt.details.length > 0 ? (
                            <List dense sx={{ p: 0 }}>
                              {receipt.details.map((d, i) => (
                                <ListItem key={i} sx={{ px: 0, py: 0.3 }}>
                                  <ListItemIcon sx={{ minWidth: 24 }}>
                                    <AttachMoneyIcon sx={{ fontSize: 16, color: '#7c3aed' }} />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={
                                      <Typography variant="caption">
                                        {d.concept} {d.amountPaid ? `- ${formatCurrency(d.amountPaid)}` : ''}
                                      </Typography>
                                    }
                                  />
                                </ListItem>
                              ))}
                            </List>
                          ) : (
                            <Typography variant="caption" sx={{ color: '#666' }}>
                              Sin detalles de conceptos.
                            </Typography>
                          )}

                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<LocalPrintshopTwoToneIcon />}
                              onClick={async () => {
                                // Tu lógica para imprimir recibo
                                messageApi.success('Recibo enviado a impresora térmica');
                              }}
                              sx={{
                                bgcolor: '#7c3aed',
                                '&:hover': { bgcolor: '#6d28d9' },
                                fontSize: '0.7rem',
                                py: 0.5
                              }}
                              disabled={isMobile}
                            >
                              Imprimir Recibo
                            </Button>
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </Stack>
                ) : (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      textAlign: 'center',
                      borderRadius: 2,
                      backgroundColor: 'rgba(243, 232, 255, 0.6)',
                      border: '1px dashed #c4b5fd'
                    }}
                  >
                    <ReceiptIcon sx={{ fontSize: 40, color: '#c4b5fd', mb: 1 }} />
                    <Typography variant="body1" sx={{ color: '#6d28d9' }}>
                      No hay recibos de pago registrados
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<PaidIcon />}
                      onClick={handleOpenPaymentDialog}
                      sx={{
                        mt: 2,
                        borderColor: '#7c3aed',
                        color: '#7c3aed',
                        '&:hover': { borderColor: '#6d28d9', backgroundColor: 'rgba(124, 58, 237, 0.04)' }
                      }}
                    >
                      Registrar Pago
                    </Button>
                  </Paper>
                )}
              </Box>
            )}

            {/* Sección de notas de débito para móvil */}
            {tabValue === 3 && invoice?.debitNotes && invoice.debitNotes.length > 0 && (
              <Paper
                elevation={2}
                sx={{
                  mt: 3,
                  mb: 3,
                  p: 2,
                  borderRadius: 3,
                  border: '1px solid #e0e7ff',
                  background: 'linear-gradient(90deg, #fdf2f8 60%, #f3e8ff 100%)'
                }}
              >
                <Typography variant="h6" sx={{ color: '#b319d2ff', fontWeight: 700, mb: 2 }}>
                  Notas de Débito
                </Typography>
                <Stack spacing={2}>
                  {invoice.debitNotes.map((note, idx) => (
                    <Accordion
                      key={note.debitNoteID || idx}
                      sx={{
                        boxShadow: 'none',
                        border: '1px solid #ddd6fe',
                        borderRadius: '8px !important',
                        '&:before': { display: 'none' }
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMore sx={{ color: '#b319d2ff' }} />}
                        sx={{
                          backgroundColor: 'rgba(243, 232, 255, 0.15)',
                          borderRadius: '8px',
                          '&.Mui-expanded': {
                            borderBottomLeftRadius: 0,
                            borderBottomRightRadius: 0,
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Grid container spacing={1}>
                            <Grid item xs={12}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <TrendingUpIcon sx={{ color: '#b319d2ff', fontSize: 20, mr: 1 }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#4b0082' }}>
                                  {note.debitNoteNumber || `Nota de Débito #${note.debitNoteID}`}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography variant="caption" sx={{ color: '#666', ml: 1 }}>
                                Fecha: {note.createdAt ? new Date(note.createdAt).toLocaleDateString('es-DO') : ''}
                              </Typography>
                            </Grid>
                          </Grid>
                          <Chip
                            label={note.paymentStatus?.toUpperCase() || 'N/D'}
                            size="small"
                            color={note.paymentStatus === 'pagado' ? 'success' : note.paymentStatus === 'pendiente' ? 'warning' : 'default'}
                            sx={{ ml: 1, fontWeight: 600, fontSize: '0.7rem', height: 20 }}
                          />
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ bgcolor: '#f8fafc', pt: 2 }}>
                        <Typography variant="body2" sx={{ color: '#4b0082', fontWeight: 600, mb: 1 }}>
                          Motivo general: {note.reason}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                          Registrado por: <b>{note.employeeName || 'No registrado'}</b>
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                          Cliente: <b>{note.clientName || 'No registrado'}</b>
                        </Typography>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="body2" sx={{ color: '#4b0082', fontWeight: 600, mb: 1 }}>
                          Servicios afectados:
                        </Typography>
                        {note.details && note.details.length > 0 ? (
                          <List dense sx={{ p: 0 }}>
                            {note.details.map((d, i) => (
                              <ListItem key={i} sx={{ px: 0, py: 0.5, alignItems: 'flex-start' }}>
                                <ListItemIcon sx={{ minWidth: 24 }}>
                                  <TrendingUpIcon sx={{ fontSize: 16, color: '#b319d2ff' }} />
                                </ListItemIcon>
                                <ListItemText
                                  primary={
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      {d.serviceName}
                                    </Typography>
                                  }
                                  secondary={
                                    <Box>
                                      <Typography variant="caption" sx={{ color: '#666' }}>
                                        Motivo: {d.concept}<br />
                                        Cantidad: {d.serviceQty} | Precio: {formatCurrency(parseNumber(d.servicePrice))}<br />
                                        {d.discountApplied > 0 && (
                                          <>Descuento: {d.discountApplied}%<br /></>
                                        )}
                                        Monto a debitar: <span style={{ color: '#d97706', fontWeight: 700 }}>{formatCurrency(d.amount)}</span>
                                      </Typography>
                                      {d.newServiceID && d.newServiceName && (
                                        <Box sx={{ mt: 0.5 }}>
                                          <Chip
                                            label="Nuevo servicio creado"
                                            color="primary"
                                            size="small"
                                            sx={{ fontWeight: 600, fontSize: '0.65rem', height: 18, px: 1, borderRadius: '8px' }}
                                          />
                                          <Typography variant="caption" sx={{ color: '#2563eb', ml: 1 }}>
                                            {d.newServiceName} ({d.newServiceQty} × {formatCurrency(d.newServicePrice)})
                                          </Typography>
                                        </Box>
                                      )}
                                    </Box>
                                  }
                                />
                              </ListItem>
                            ))}
                          </List>
                        ) : (
                          <Typography variant="body2" sx={{ color: '#666' }}>Sin detalles de servicios.</Typography>
                        )}
                        <Divider sx={{ my: 1 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <IconButton
                            size="small"
                            color="primary"
                            title="Visualizar nota de débito"
                            sx={{
                              bgcolor: '#ede9fe',
                              '&:hover': { bgcolor: '#c7d2fe' }
                            }}
                            onClick={() => window.open(`/billing/debit-note/${note.debitNoteID}`, '_blank', 'noopener,noreferrer')}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Stack>
              </Paper>
            )}

            {/* Sección de notas de crédito para móvil */}
            {tabValue === 4 && invoice?.creditNotes && invoice.creditNotes.length > 0 && (
              <Paper
                elevation={2}
                sx={{
                  mt: 3,
                  mb: 3,
                  p: 2,
                  borderRadius: 3,
                  border: '2px solid #2563eb',
                  background: 'linear-gradient(90deg, #e0e7ff 60%, #fdf2f8 100%)'
                }}
              >
                <Typography variant="h6" sx={{ color: '#2563eb', fontWeight: 700, mb: 2 }}>
                  Notas de Crédito
                </Typography>
                <Stack spacing={2}>
                  {invoice.creditNotes.map((note, idx) => (
                    <Accordion
                      key={note.creditNoteID || idx}
                      sx={{
                        boxShadow: 'none',
                        border: '1px solid #90cdf4',
                        borderRadius: '8px !important',
                        '&:before': { display: 'none' }
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMore sx={{ color: '#2563eb' }} />}
                        sx={{
                          backgroundColor: 'rgba(224, 231, 255, 0.15)',
                          borderRadius: '8px',
                          '&.Mui-expanded': {
                            borderBottomLeftRadius: 0,
                            borderBottomRightRadius: 0,
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Grid container spacing={1}>
                            <Grid item xs={12}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <TrendingUpIcon sx={{ color: '#2563eb', fontSize: 20, mr: 1 }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2563eb' }}>
                                  {note.creditNoteNumber || `Nota de Crédito #${note.creditNoteID}`}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography variant="caption" sx={{ color: '#666', ml: 1 }}>
                                Fecha: {note.createdAt ? new Date(note.createdAt).toLocaleDateString('es-DO') : ''}
                              </Typography>
                            </Grid>
                          </Grid>
                          <Chip
                            label={note.paymentStatus?.toUpperCase() || 'N/D'}
                            size="small"
                            color={note.paymentStatus === 'pagado' ? 'success' : note.paymentStatus === 'pendiente' ? 'warning' : 'default'}
                            sx={{ ml: 1, fontWeight: 600, fontSize: '0.7rem', height: 20 }}
                          />
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ bgcolor: '#f8fafc', pt: 2 }}>
                        <Typography variant="body2" sx={{ color: '#2563eb', fontWeight: 600, mb: 1 }}>
                          Motivo general: {note.reason}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                          Registrado por: <b>{note.employeeName || 'No registrado'}</b>
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                          Cliente: <b>{note.clientName || 'No registrado'}</b>
                        </Typography>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="body2" sx={{ color: '#2563eb', fontWeight: 600, mb: 1 }}>
                          Servicios afectados:
                        </Typography>
                        {note.details && note.details.length > 0 ? (
                          <List dense sx={{ p: 0 }}>
                            {note.details.map((d, i) => (
                              <ListItem key={i} sx={{ px: 0, py: 0.5, alignItems: 'flex-start' }}>
                                <ListItemIcon sx={{ minWidth: 24 }}>
                                  <TrendingUpIcon sx={{ fontSize: 16, color: '#2563eb' }} />
                                </ListItemIcon>
                                <ListItemText
                                  primary={
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#2563eb' }}>
                                      {d.serviceName}
                                    </Typography>
                                  }
                                  secondary={
                                    <Box>
                                      <Typography variant="caption" sx={{ color: '#666' }}>
                                        Motivo: {d.concept}<br />
                                        Cantidad: {d.serviceQty} | Precio: {formatCurrency(parseNumber(d.servicePrice))}<br />
                                        {d.discountApplied > 0 && (
                                          <>Descuento: {d.discountApplied}%<br /></>
                                        )}
                                        Monto a acreditar: <span style={{ color: '#2563eb', fontWeight: 700 }}>{formatCurrency(d.amount)}</span>
                                      </Typography>
                                      {d.newServiceID && d.newServiceName && (
                                        <Box sx={{ mt: 0.5 }}>
                                          <Chip
                                            label="Nuevo servicio creado"
                                            color="primary"
                                            size="small"
                                            sx={{ fontWeight: 600, fontSize: '0.65rem', height: 18, px: 1, borderRadius: '8px' }}
                                          />
                                          <Typography variant="caption" sx={{ color: '#2563eb', ml: 1 }}>
                                            {d.newServiceName} ({d.newServiceQty} × {formatCurrency(d.newServicePrice)})
                                          </Typography>
                                        </Box>
                                      )}
                                    </Box>
                                  }
                                />
                              </ListItem>
                            ))}
                          </List>
                        ) : (
                          <Typography variant="body2" sx={{ color: '#666' }}>Sin detalles de servicios.</Typography>
                        )}
                        <Divider sx={{ my: 1 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <IconButton
                            size="small"
                            color="primary"
                            title="Visualizar nota de crédito"
                            sx={{
                              bgcolor: '#e0e7ff',
                              '&:hover': { bgcolor: '#c7d2fe' }
                            }}
                            onClick={() => window.open(`/billing/credit-note/${note.creditNoteID}`, '_blank', 'noopener,noreferrer')}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Stack>
              </Paper>
            )}

            {/* Menú de opciones */}
            <Menu
              anchorEl={anchorElMobile}
              open={openMobile}
              onClose={handleMenuCloseMobile}
              MenuListProps={{
                'aria-labelledby': 'basic-button',
              }}
              sx={{
                '& .MuiMenu-paper': {
                  backgroundColor: 'rgba(243, 232, 255, 1)',
                  borderRadius: 2,
                  boxShadow: '0 4px 6px rgba(124, 58, 237, 0.1)',
                },
              }}
            >
              <MenuItem sx={{ color: '#7c3aed' }}
                onClick={handleOpenPaymentDialog}
                disabled={invoice?.paymentStatus === 'pagado'}
              >
                <PaidIcon sx={{ mr: 1 }} />
                Registrar Pagos
              </MenuItem>
              <MenuItem sx={{ color: '#7c3aed' }} onClick={handleOpenEditInvoiceDialog}>
                <TextSnippetSharp sx={{ mr: 1 }} />
                Modificar Factura
              </MenuItem>
            </Menu>
          </Box>
          :
          <Container>
            {/* Botones de acción */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
              <Stack direction="row" spacing={2}>
                <Button
                  onClick={() => navigate(-1)}
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  sx={{
                    borderColor: '#9ca3af',
                    color: '#4b5563',
                    '&:hover': { borderColor: '#6b7280', bgcolor: 'rgba(107, 114, 128, 0.04)' }
                  }}
                >
                  Volver a Facturas
                </Button>

                <Button
                  variant="contained"
                  startIcon={<PaidIcon />}
                  onClick={handleOpenPaymentDialog}
                  sx={{
                    bgcolor: '#7c3aed',
                    '&:hover': { bgcolor: '#6d28d9' }
                  }}
                >
                  Registrar Pago
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<PrintIcon />}
                  onClick={handlePrintA4}
                  sx={{
                    borderColor: '#7c3aed',
                    color: '#7c3aed',
                    '&:hover': { borderColor: '#6d28d9', bgcolor: 'rgba(124, 58, 237, 0.04)' }
                  }}
                >
                  Imprimir A4
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<LocalPrintshopTwoToneIcon />}
                  onClick={handleDirectThermalPrint}
                  disabled={configLoading || !printerConfig}
                  sx={{
                    borderColor: '#f759ab',
                    color: '#f759ab',
                    '&:hover': { borderColor: '#e64980', bgcolor: 'rgba(247, 89, 171, 0.04)' }
                  }}
                >
                  Imprimir Térmica
                </Button>
              </Stack>
            </Box>

            {/* Área principal para imprimir */}
            <div className='invoice-A4-sheet' ref={componentRef}>
              <Paper
                elevation={3}
                sx={{
                  p: 4,
                  mb: 4,
                  borderRadius: 3,
                  border: '1px solid rgba(124, 58, 237, 0.1)',
                }}
              >
                {/* Encabezado de factura */}
                <Box sx={{ mb: 4 }}>
                  <Grid container spacing={2} alignItems="center">
                    {/* Menu button for additional options */}
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end', width: '100%', borderRadius: 1, p: 0, backgroundColor: 'rgba(243, 232, 255, 0.3)' }}>
                        <IconButton id='menu-button' onClick={handleMenuClickDesktop} sx={{ color: '#7c3aed' }}>
                          <Tooltip title="Más opciones">
                            <MoreVertIcon sx={{ color: '#7c3aed', fontSize: 30 }} />
                          </Tooltip>
                        </IconButton>
                        <Menu
                          id="invoice-menu"
                          anchorEl={anchorElDesktop}
                          open={openDesktop}
                          onClose={handleMenuCloseDesktop}
                          slotProps={{
                            list: {
                              'aria-labelledby': 'basic-button',
                            },
                          }}
                          sx={{
                            '& .MuiMenu-paper': {
                              backgroundColor: 'rgba(243, 232, 255, 1)',
                              borderRadius: 2,
                              boxShadow: '0 4px 6px rgba(124, 58, 237, 0.1)',
                            },
                          }}
                        >
                          <MenuItem sx={{ color: '#7c3aed' }}
                            onClick={() => {
                              handleMenuCloseDesktop();
                              handleOpenPaymentDialog();
                            }}
                          >
                            <PaidIcon sx={{ mr: 1 }} />
                            Registrar Pagos
                          </MenuItem>
                          <MenuItem sx={{ color: '#7c3aed' }} onClick={() => {
                            handleMenuCloseDesktop();
                            handleOpenEditInvoiceDialog();
                          }}>
                            <TextSnippetSharp sx={{ mr: 1 }} />
                            Modificar Factura
                          </MenuItem>
                        </Menu>
                      </Box>
                    </Grid>

                    {/* Título */}
                    <Grid item xs={8}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ReceiptLongIcon sx={{ color: '#f759ab', fontSize: 40, mr: 2 }} />
                        <Box>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: '#4b0082' }}>
                            D'Princess
                          </Typography>
                          <Typography variant="subtitle1" sx={{ color: '#7c3aed' }}>
                            Documento de Facturación
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    {/* Número de factura */}
                    <Grid item xs={4} sx={{ textAlign: 'right' }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#4b0082' }}>
                        Factura #{invoice?.invoiceNumber || '0000'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#666' }}>
                        {invoice?.invoiceDate
                          ? new Date(invoice.invoiceDate).toLocaleDateString('es-DO', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })
                          : 'Fecha no registrada'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* Cliente y Detalles de factura */}
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={6}>
                    <Card
                      variant="outlined"
                      sx={{
                        bgcolor: 'rgba(243, 232, 255, 0.3)',
                        borderColor: 'rgba(124, 58, 237, 0.1)',
                        height: '100%'
                      }}
                    >
                      <CardContent>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 700,
                            color: '#4b0082',
                            display: 'flex',
                            alignItems: 'center',
                            mb: 1.5
                          }}
                        >
                          <PersonIcon sx={{ mr: 1, fontSize: 18 }} />
                          CLIENTE
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {invoice?.clientName || 'Cliente no registrado'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                          {invoice?.address || 'Dirección no registrada'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                          {invoice?.phoneNumber || 'Teléfono no registrado'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666' }}>
                          {invoice?.clientEmail || 'Email no registrado'}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card
                      variant="outlined"
                      sx={{
                        bgcolor: 'rgba(243, 232, 255, 0.3)',
                        borderColor: 'rgba(124, 58, 237, 0.1)',
                        height: '100%'
                      }}
                    >
                      <CardContent>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 700,
                            color: '#4b0082',
                            display: 'flex',
                            alignItems: 'center',
                            mb: 1.5
                          }}
                        >
                          <InfoIcon sx={{ mr: 1, fontSize: 18 }} />
                          DETALLES DE LA FACTURA
                        </Typography>
                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <Typography variant="body2" sx={{ color: '#666' }}>
                              Estado:
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Chip
                              label={invoice?.paymentStatus?.toUpperCase() || 'PENDIENTE'}
                              size="small"
                              color={invoice?.paymentStatus === "pagado" ? "success" : "warning"}
                              sx={{ fontWeight: 600 }}
                            />
                          </Grid>

                          <Grid item xs={6}>
                            <Typography variant="body2" sx={{ color: '#666' }}>
                              Emitida por:
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {invoice?.employeeName || 'No registrado'}
                            </Typography>
                          </Grid>

                          <Grid item xs={6}>
                            <Typography variant="body2" sx={{ color: '#666' }}>
                              Fecha de emisión:
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {invoice?.invoiceDate
                                ? new Date(invoice.invoiceDate).toLocaleDateString()
                                : 'No registrada'}
                            </Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Evento */}
                {(invoice?.eventTitle || invoice?.eventLocation) && (
                  <Card
                    variant="outlined"
                    sx={{
                      mb: 3,
                      bgcolor: 'rgba(247, 89, 171, 0.05)',
                      borderColor: 'rgba(247, 89, 171, 0.2)'
                    }}
                  >
                    <CardContent>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 700,
                          color: '#f759ab',
                          display: 'flex',
                          alignItems: 'center',
                          mb: 1.5
                        }}
                      >
                        <EventNoteIcon sx={{ mr: 1, fontSize: 18 }} />
                        INFORMACIÓN DEL EVENTO
                      </Typography>

                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                            Evento: <span style={{ color: '#4b0082', fontWeight: 600 }}>
                              {invoice?.eventTitle || 'No registrado'}
                            </span>
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                            Lugar: <span style={{ color: '#4b0082', fontWeight: 600 }}>
                              {invoice?.eventLocation || 'No registrado'}
                            </span>
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                            Fecha de inicio: <span style={{ color: '#4b0082', fontWeight: 600 }}>
                              {invoice?.eventDateStart
                                ? new Date(invoice.eventDateStart).toLocaleDateString('es-DO', {
                                  year: 'numeric',
                                  month: '2-digit',
                                  day: '2-digit',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  hour12: true
                                })
                                : 'No registrada'}
                            </span>
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                            Fecha de finalización: <span style={{ color: '#4b0082', fontWeight: 600 }}>
                              {invoice?.eventDateEnd
                                ? new Date(invoice.eventDateEnd).toLocaleDateString('es-DO', {
                                  year: 'numeric',
                                  month: '2-digit',
                                  day: '2-digit',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  hour12: true
                                })
                                : 'No registrada'}
                            </span>
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                )}

                {/* Servicios */}
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: '#4b0082',
                    mb: 2
                  }}
                >
                  DETALLES DE SERVICIOS
                </Typography>

                <Table sx={{ mb: 3, minWidth: 650 }}>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'rgba(124, 58, 237, 0.05)' }}>
                      <TableCell sx={{ fontWeight: 700, color: '#4b0082' }}>Servicio</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: '#4b0082' }}>Cantidad</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: '#4b0082' }}>Precio</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: '#4b0082' }}>Pagado</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: '#4b0082' }}>Pendiente</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: '#4b0082' }}>Estado</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Services */}
                    {invoice.invoiceDetails &&
                      invoice.invoiceDetails
                        .map((detail, index) => (
                          <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                            <TableCell component="th" scope="row" sx={{ borderBottom: '1px solid rgba(124, 58, 237, 0.1)' }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {detail.serviceName} {detail.serviceID === 24 && <Chip label="Paquete" size="small" sx={{ ml: 1, fontSize: '0.7rem' }} />}
                                {detail.historial && (
                                  <span style={{ color: '#d97706', fontSize: '0.8rem', marginLeft: 8 }}>
                                    <br />{detail.historial}
                                  </span>
                                )}
                              </Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ borderBottom: '1px solid rgba(124, 58, 237, 0.1)' }}>
                              {detail.serviceQty}
                            </TableCell>
                            <TableCell align="right" sx={{ borderBottom: '1px solid rgba(124, 58, 237, 0.1)' }}>
                              {formatCurrency(
                                parseNumber(detail.servicePrice) * parseNumber(detail.serviceQty) * (1 - (detail.discountApplied ? detail.discountApplied / 100 : 0))
                              )}
                              {detail.discountApplied > 0 && (
                                <span style={{ color: '#666', fontSize: '0.8rem' }}>
                                  <br />
                                  {detail.discountApplied}% de desc. aplicado
                                </span>
                              )}
                              <br />
                              <span style={{ color: '#666', fontSize: '0.8rem' }}>
                                {`(Precio unitario ${formatCurrency(parseNumber(detail.servicePrice))})`}
                              </span>
                            </TableCell>
                            <TableCell align="right" sx={{ borderBottom: '1px solid rgba(124, 58, 237, 0.1)' }}>
                              {formatCurrency(detail.pagado)}
                            </TableCell>
                            <TableCell align="right" sx={{
                              borderBottom: '1px solid rgba(124, 58, 237, 0.1)',
                              fontWeight: 600
                            }}>
                              <Typography
                                sx={{ color: detail.pendiente > 0 ? '#d97706' : '#22c55e', textDecoration: detail.correctionType === 'credit' ? 'line-through' : 'none' }}
                              >
                                {formatCurrency(detail.pendiente)}
                              </Typography>
                              {
                                detail.correctionType === 'credit' && detail.creditNoteAmount > 0 && (
                                  <span style={{ color: '#666', fontSize: '0.6rem' }}>
                                    {`- ${formatCurrency(detail.creditNoteAmount)} acreditados`}
                                  </span>
                                )
                              }
                              {
                                detail.correctionType === 'credit' && detail.creditNoteAmount > 0 && (
                                  <Typography variant="caption" sx={{ color: formatCurrency(parseNumber(detail.pendiente) - parseNumber(detail.creditNoteAmount)) > 0 ? '#d97706' : '#22c55e', fontSize: '0.9rem', mt: 0.5 }}>
                                    <br />
                                    {detail.pendiente > 0 ? formatCurrency(parseNumber(detail.pendiente) - parseNumber(detail.creditNoteAmount)) : formatCurrency(detail.pendiente)}
                                  </Typography>
                                )
                              }
                            </TableCell>
                            <TableCell align="center" sx={{ borderBottom: '1px solid rgba(124, 58, 237, 0.1)' }}>
                              <Chip
                                label={detail.estado === 'pagado' ? 'Pagado' : detail.estado === 'corregido' ? 'Corregido' : 'Pendiente'}
                                color={detail.estado === 'pagado' ? 'success' : detail.estado === 'corregido' ? 'warning' : 'default'}
                                size="small"
                                sx={{ fontWeight: 600 }}
                              />
                            </TableCell>
                          </TableRow>
                        ))
                    }
                  </TableBody>
                </Table>

                {/* Totales */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 4 }}>
                  <Card
                    variant="outlined"
                    sx={{
                      width: { xs: '100%', sm: '50%', md: '40%' },
                      bgcolor: 'rgba(124, 58, 237, 0.03)',
                      borderColor: 'rgba(124, 58, 237, 0.1)'
                    }}
                  >
                    <CardContent>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="body2" sx={{ color: '#666' }}>
                            Subtotal:
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sx={{ textAlign: 'right' }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, textDecoration: (invoice.totalCreditAmount > 0) ? 'line-through' : 'normal' }}>
                            {formatCurrency(parseNumber(invoice?.totalAmount))}
                          </Typography>
                          {invoice.totalCreditAmount > 0 && (
                            <span style={{ color: '#d97706', fontSize: '0.8rem', marginLeft: '0.5rem' }}>
                              {`- ${formatCurrency(invoice.totalCreditAmount)} acreditados`}
                            </span>
                          )}
                          {
                            (invoice.totalCreditAmount > 0 || invoice.totalDebitAmount > 0) && (
                              <span style={{ color: '#0c91cfff', fontSize: '1rem', fontWeight: 500, marginLeft: '0.5rem' }}>
                                <br />
                                {`${formatCurrency(parseNumber(invoice?.totalAmount) - invoice.totalCreditAmount)}`}
                              </span>
                            )
                          }
                        </Grid>

                        <Grid item xs={6}>
                          <Typography variant="body2" sx={{ color: '#666' }}>
                            Total pagado:
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sx={{ textAlign: 'right' }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: '#22c55e', textDecoration: (invoice.totalPaidWithCreditAmount !== invoice.paidAmount) ? 'line-through' : 'normal' }}>
                            {formatCurrency(parseNumber(invoice?.paidAmount))}
                          </Typography>
                          {invoice.totalPaidWithCreditAmount !== invoice.paidAmount && (
                            <Typography variant="body2" sx={{ fontWeight: 800, color: '#22c55e' }}>
                              {formatCurrency(parseNumber(invoice?.totalPaidWithCreditAmount))}
                            </Typography>
                          )}
                        </Grid>

                        <Grid item xs={6}>
                          <Typography variant="body2" sx={{ color: '#666', fontWeight: 600 }}>
                            Total pendiente:
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sx={{ textAlign: 'right' }}>
                          <Typography variant="body1" sx={{ fontWeight: (invoice?.pendingAmount !== invoice?.totalPendingWithCreditAmount) ? 500 : 700, color: '#d97706', textDecoration: (invoice?.pendingAmount !== invoice?.totalPendingWithCreditAmount) ? 'line-through' : 'normal' }}>
                            {formatCurrency(parseNumber(invoice?.pendingAmount))}
                          </Typography>
                          {invoice?.pendingAmount !== invoice?.totalPendingWithCreditAmount && (
                            <Typography variant="body1" sx={{ fontWeight: 800, color: '#d97706' }}>
                              {formatCurrency(parseNumber(invoice?.totalPendingWithCreditAmount))}
                            </Typography>
                          )}
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Box>

                {/* Pie de página */}
                <Box sx={{ pt: 2, borderTop: '1px dashed rgba(124, 58, 237, 0.2)' }}>
                  <Typography variant="body2" sx={{ textAlign: 'center', color: '#666', fontStyle: 'italic' }}>
                    Gracias por confiar en D'Princess Foto Film.
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: '#888', mt: 1 }}>
                    Este documento sirve como comprobante de pago.
                  </Typography>
                </Box>
              </Paper>
            </div>

            {/* Sección de recibos */}
            {paymentReceipt && paymentReceipt.length > 0 && (
              <Paper
                elevation={2}
                sx={{
                  mt: 3,
                  mb: 3,
                  p: 2,
                  borderRadius: 3,
                  border: '1px solid #e0e7ff',
                  background: 'linear-gradient(90deg, #f3e8ff 60%, #fdf2f8 100%)'
                }}
              >
                <Typography variant="h6" sx={{ color: '#7c3aed', fontWeight: 700, mb: 2 }}>
                  Recibos de Pago
                </Typography>

                <Stack spacing={2}>
                  {paymentReceipt.map((receipt, idx) => (
                    <Paper
                      key={receipt.receiptDocumentNumber || idx}
                      elevation={1}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: '1px solid #ddd6fe',
                        background: expandedReceipt === idx ? '#f3e8ff' : '#fff',
                        position: 'relative'
                      }}
                    >
                      {/* Botón de imprimir arriba a la derecha */}
                      <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={async () => {
                            const escposData = await generatePaymentReceipt48(receipt, invoice);
                            const printResult = await handleDirectThermalPrinter(escposData);

                            if (printResult.success) {
                              messageApi.success({
                                content: printResult.message,
                                key: 'thermalPrint',
                                duration: 3
                              });
                            } else {
                              messageApi.error({
                                content: printResult.message,
                                key: 'thermalPrint',
                                duration: 5
                              });
                            }
                          }}
                          title="Imprimir recibo térmico directamente"
                          sx={{
                            bgcolor: '#ede9fe',
                            '&:hover': { bgcolor: '#c7d2fe' }
                          }}
                        >
                          <LocalPrintshopTwoToneIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      {/* Resumen rápido */}
                      <Grid container spacing={1} alignItems="center">
                        <Grid item xs={12} sm={10}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ReceiptIcon sx={{ color: '#7c3aed' }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#4b0082' }}>
                              {receipt.receiptDocumentNumber || 'Recibo'}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#666', ml: 2 }}>
                              {receipt.transactionDate
                                ? new Date(receipt.transactionDate).toLocaleString('es-DO')
                                : ''}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#666', ml: 2 }}>
                              Monto: <span style={{ color: '#22c55e', fontWeight: 700 }}>{formatCurrency(receipt.totalAmountReceived)}</span>
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#666', ml: 2 }}>
                              Método: <span style={{ fontWeight: 500 }}>{receipt.paymentMethod?.toUpperCase() || 'N/D'}</span>
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sx={{ textAlign: { xs: 'center', sm: 'center' } }}>
                          <Button
                            size="small"
                            variant={expandedReceipt === idx ? "contained" : "outlined"}
                            startIcon={<VisibilityIcon />}
                            sx={{
                              color: expandedReceipt === idx ? "#fff" : "#7c3aed",
                              bgcolor: expandedReceipt === idx ? "#7c3aed" : "transparent",
                              borderColor: "#7c3aed",
                              fontWeight: 600,
                              '&:hover': {
                                bgcolor: "#6d28d9",
                                color: "#fff",
                                borderColor: "#6d28d9"
                              }
                            }}
                            onClick={() => setExpandedReceipt(expandedReceipt === idx ? null : idx)}
                          >
                            {expandedReceipt === idx ? "Ocultar Detalle" : "Ver Detalle"}
                          </Button>
                        </Grid>
                      </Grid>
                      {/* Detalle expandible */}
                      {expandedReceipt === idx && (
                        <Box sx={{ mt: 2, bgcolor: '#f8fafc', borderRadius: 2, p: 2 }}>
                          <Typography variant="subtitle2" sx={{ color: '#7c3aed', fontWeight: 600, mb: 1 }}>
                            Detalle del Recibo
                          </Typography>
                          <Grid container spacing={1}>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2"><b>No. Recibo:</b> {receipt.receiptDocumentNumber}</Typography>
                              <Typography variant="body2"><b>Fecha:</b> {receipt.transactionDate ? new Date(receipt.transactionDate).toLocaleString('es-DO') : ''}</Typography>
                              <Typography variant="body2"><b>Monto recibido:</b> {formatCurrency(receipt.totalAmountReceived)}</Typography>
                              <Typography variant="body2"><b>Método de pago:</b> {receipt.paymentMethod?.toUpperCase() || 'N/D'}</Typography>
                              {receipt.referenceNumber && (
                                <Typography variant="body2"><b>Referencia:</b> {receipt.referenceNumber}</Typography>
                              )}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              {
                                receipt.paymentMethod === 'transferencia' && (
                                  <Typography variant="body2"><b>Banco:</b> {banksOptions.find(bank => bank.id === receipt.bank)?.bank || 'No registrado'}</Typography>
                                )
                              }
                              <Typography variant="body2"><b>Registrado por:</b> {receipt.employeeName || 'No registrado'}</Typography>
                              <Typography variant="body2"><b>Notas:</b> {receipt.notes || 'Sin notas'}</Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Divider sx={{ my: 1 }} />
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#4b0082', mb: 1 }}>
                                Conceptos Pagados:
                              </Typography>
                              {receipt.details && receipt.details.length > 0 ? (
                                <ul style={{ margin: 0, paddingLeft: 18 }}>
                                  {receipt.details.map((d, i) => (
                                    <li key={i}>
                                      <Typography variant="body2">
                                        {d.concept} {d.amountPaid ? `- ${formatCurrency(d.amountPaid)}` : ''}
                                      </Typography>
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <Typography variant="body2" sx={{ color: '#666' }}>Sin detalles de conceptos.</Typography>
                              )}
                            </Grid>
                          </Grid>
                        </Box>
                      )}
                    </Paper>
                  ))}
                </Stack>
              </Paper>
            )}

            {/* Sección de notas de débito */}
            {invoice?.debitNotes && invoice.debitNotes.length > 0 && (
              <Paper
                elevation={2}
                sx={{
                  mt: 3,
                  mb: 3,
                  p: 2,
                  borderRadius: 3,
                  border: '1px solid #e0e7ff',
                  background: 'linear-gradient(90deg, #fdf2f8 60%, #f3e8ff 100%)'
                }}
              >
                <Typography variant="h6" sx={{ color: '#b319d2ff', fontWeight: 700, mb: 2 }}>
                  Notas de Débito
                </Typography>

                <Stack spacing={2}>
                  {invoice.debitNotes.map((note, idx) => (
                    <Paper
                      key={note.debitNoteID || idx}
                      elevation={1}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: '1px solid #ddd6fe',
                        background: '#fff',
                        position: 'relative'
                      }}
                    >
                      {/* Encabezado de la nota */}
                      <Grid container spacing={1} alignItems="center">
                        <Grid item xs={10} sm={10}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TrendingUpIcon sx={{ color: '#b319d2ff' }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#4b0082' }}>
                              {note.debitNoteNumber || `Nota de Débito #${note.debitNoteID}`}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#666', ml: 2 }}>
                              {note.createdAt
                                ? new Date(note.createdAt).toLocaleString('es-DO')
                                : ''}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#666', ml: 2 }}>
                              Monto: <span style={{ color: '#d97706', fontWeight: 700 }}>{formatCurrency(note.amount)}</span>
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#666', ml: 2 }}>
                              Estado: <span style={{ fontWeight: 500 }}>{note.paymentStatus?.toUpperCase() || 'N/D'}</span>
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={2} sx={{ textAlign: { xs: 'right', sm: 'right' } }}>
                          <IconButton size="small"
                            color="primary"
                            title="Visualizar nota de débito"
                            sx={{
                              bgcolor: '#ede9fe',
                              '&:hover': { bgcolor: '#c7d2fe' }
                            }}
                            onClick={() => window.open(`/billing/debit-note/${note.debitNoteID}`, '_blank', 'noopener,noreferrer')}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Grid>
                      </Grid>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="body2" sx={{ color: '#4b0082', fontWeight: 600, mb: 1 }}>
                        Motivo general: {note.reason}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                        Registrado por: <b>{note.employeeName || 'No registrado'}</b>
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                        Cliente: <b>{note.clientName || 'No registrado'}</b>
                      </Typography>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="body2" sx={{ color: '#4b0082', fontWeight: 600, mb: 1 }}>
                        Servicios afectados:
                      </Typography>
                      {/* Detalle de servicios afectados */}
                      {note.details && note.details.length > 0 ? (
                        <Table size="small" sx={{ mb: 1 }}>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ color: '#b319d2ff', fontWeight: 700 }}>Servicio original</TableCell>
                              <TableCell sx={{ color: '#b319d2ff', fontWeight: 700 }}>Motivo</TableCell>
                              <TableCell sx={{ color: '#b319d2ff', fontWeight: 700 }}>Nuevo servicio / Cambio</TableCell>
                              <TableCell sx={{ color: '#b319d2ff', fontWeight: 700 }}>Monto a debitar</TableCell>
                              <TableCell sx={{ color: '#b319d2ff', fontWeight: 700 }}>Estado de pago</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {note.details.map((d, i) => (
                              <TableRow key={i}>
                                {/* Servicio original */}
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {d.serviceName}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: '#666' }}>
                                    Cantidad: {d.serviceQty} <br />
                                    Precio: {formatCurrency(parseNumber(d.servicePrice))} <br />
                                    {d.discountApplied > 0 && (
                                      <Typography variant="caption" sx={{ color: '#666' }}>
                                        Descuento aplicado: {formatCurrency(d.discountApplied)}
                                        <br />
                                      </Typography>
                                    )}
                                    Total: {formatCurrency(parseNumber(d.servicePrice) * d.serviceQty * (100 - (d.discountApplied || 0)) / 100)}
                                  </Typography>
                                </TableCell>
                                {/* Motivo */}
                                <TableCell>
                                  <Typography variant="body2">{d.concept}</Typography>
                                </TableCell>
                                {/* Nuevo servicio o cambio */}
                                <TableCell>
                                  {d.newServiceID && d.newServiceName ? (
                                    <Box>
                                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#2563eb' }}>
                                        {d.newServiceName}
                                      </Typography>
                                      <Chip
                                        label="Nuevo servicio creado"
                                        color="primary"
                                        size='small'
                                        sx={{ mt: 0.5, fontWeight: 600, fontSize: '0.68rem', height: 22, px: 1, py: 0, minHeight: '18px', borderRadius: '8px' }}
                                      />
                                      <br />
                                      <Typography variant="caption" sx={{ color: '#666' }}>
                                        Cantidad: {d.newServiceQty} <br />
                                        Precio: {formatCurrency(d.newServicePrice)} <br />
                                        {d.newServiceDiscount > 0 && (
                                          <Typography variant="caption" sx={{ color: '#666' }}>
                                            Descuento aplicado: {formatCurrency(d.newServiceDiscount)}
                                            <br />
                                          </Typography>
                                        )}
                                        Total: {formatCurrency(d.newServicePrice * d.newServiceQty * (100 - (d.newServiceDiscount || 0)) / 100)}
                                      </Typography>
                                      <br />
                                    </Box>
                                  ) : (
                                    <Box>
                                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#d97706' }}>
                                        Actualización de precio
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: '#666' }}>
                                        Nuevo precio: {formatCurrency(parseNumber(d.servicePrice) + parseNumber(d.amount))}
                                      </Typography>
                                      <br />
                                      <Chip
                                        label="Aumento de precio"
                                        color="warning"
                                        size="small"
                                        sx={{ mt: 1, fontWeight: 600 }}
                                      />
                                    </Box>
                                  )}
                                </TableCell>
                                {/* Monto a debitar */}
                                <TableCell>
                                  <span style={{ color: '#d97706', fontWeight: 700 }}>
                                    {formatCurrency(d.amount)}
                                  </span>
                                </TableCell>
                                {/* Estado */}
                                <TableCell>
                                  <Chip
                                    label={d.paymentStatus === 'pagado' ? 'Pagado' : d.paymentStatus === 'pendiente' ? 'Pendiente' : 'N/D'}
                                    color={d.paymentStatus === 'pagado' ? 'success' : d.paymentStatus === 'pendiente' ? 'warning' : 'default'}
                                    size="small"
                                    sx={{ fontWeight: 600 }}
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <Typography variant="body2" sx={{ color: '#666' }}>Sin detalles de servicios.</Typography>
                      )}
                    </Paper>
                  ))}
                </Stack>
              </Paper>
            )}

            {/* Sección de notas de crédito */}
            {invoice?.creditNotes && invoice.creditNotes.length > 0 && (
              <Paper
                elevation={2}
                sx={{
                  mt: 3,
                  mb: 3,
                  p: 2,
                  borderRadius: 3,
                  border: '1px solid #e0e7ff',
                  background: 'linear-gradient(90deg, #e0e7ff 60%, #fdf2f8 100%)' // Fondo azul claro
                }}
              >
                <Typography variant="h6" sx={{ color: '#2563eb', fontWeight: 700, mb: 2 }}>
                  Notas de Crédito
                </Typography>

                <Stack spacing={2}>
                  {invoice.creditNotes.map((note, idx) => (
                    <Paper
                      key={note.creditNoteID || idx}
                      elevation={1}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: '1px solid #ddd6fe',
                        background: '#f3f8ff', // Fondo azul muy claro
                        position: 'relative'
                      }}
                    >
                      {/* Encabezado de la nota */}
                      <Grid container spacing={1} alignItems="center">
                        <Grid item xs={10} sm={10}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TrendingUpIcon sx={{ color: '#2563eb' }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2563eb' }}>
                              {note.creditNoteNumber || `Nota de Crédito #${note.creditNoteID}`}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#2563eb', ml: 2 }}>
                              {note.createdAt
                                ? new Date(note.createdAt).toLocaleString('es-DO')
                                : ''}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#2563eb', ml: 2 }}>
                              Monto: <span style={{ color: '#2563eb', fontWeight: 700 }}>{formatCurrency(note.amount)}</span>
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#2563eb', ml: 2 }}>
                              Estado: <span style={{ fontWeight: 500 }}>{note.paymentStatus?.toUpperCase() || 'N/D'}</span>
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={2} sx={{ textAlign: { xs: 'right', sm: 'right' } }}>
                          <IconButton size="small"
                            color="primary"
                            title="Visualizar nota de crédito"
                            sx={{
                              bgcolor: '#e0e7ff',
                              '&:hover': { bgcolor: '#c7d2fe' }
                            }}
                            onClick={() => window.open(`/billing/credit-note/${note.creditNoteID}`, '_blank', 'noopener,noreferrer')}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Grid>
                      </Grid>
                      <Divider sx={{ my: 1, borderColor: '#2563eb' }} />
                      <Typography variant="body2" sx={{ color: '#2563eb', fontWeight: 600, mb: 1 }}>
                        Motivo general: {note.reason}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                        Registrado por: <b>{note.employeeName || 'No registrado'}</b>
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                        Cliente: <b>{note.clientName || 'No registrado'}</b>
                      </Typography>
                      <Divider sx={{ my: 1, borderColor: '#2563eb' }} />
                      <Typography variant="body2" sx={{ color: '#2563eb', fontWeight: 600, mb: 1 }}>
                        Servicios afectados:
                      </Typography>
                      {/* Detalle de servicios afectados */}
                      {note.details && note.details.length > 0 ? (
                        <Table size="small" sx={{ mb: 1 }}>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ color: '#2563eb', fontWeight: 700 }}>Servicio original</TableCell>
                              <TableCell sx={{ color: '#2563eb', fontWeight: 700 }}>Motivo</TableCell>
                              <TableCell sx={{ color: '#2563eb', fontWeight: 700 }}>Nuevo servicio / Cambio</TableCell>
                              <TableCell sx={{ color: '#2563eb', fontWeight: 700 }}>Monto a acreditar</TableCell>
                              <TableCell sx={{ color: '#2563eb', fontWeight: 700 }}>Estado de pago</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {note.details.map((d, i) => (
                              <TableRow key={i}>
                                {/* Servicio original */}
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#2563eb' }}>
                                    {d.serviceName}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: '#666' }}>
                                    Cantidad: {d.serviceQty} <br />
                                    Precio: {formatCurrency(parseNumber(d.servicePrice))} <br />
                                    {d.discountApplied > 0 && (
                                      <Typography variant="caption" sx={{ color: '#666' }}>
                                        Descuento aplicado: {formatCurrency(d.discountApplied)}
                                        <br />
                                      </Typography>
                                    )}
                                    Total: {formatCurrency(parseNumber(d.servicePrice) * d.serviceQty * (100 - (d.discountApplied || 0)) / 100)}
                                  </Typography>
                                </TableCell>
                                {/* Motivo */}
                                <TableCell>
                                  <Typography variant="body2">{d.concept}</Typography>
                                </TableCell>
                                {/* Nuevo servicio o cambio */}
                                <TableCell>
                                  {d.newServiceID && d.newServiceName ? (
                                    <Box>
                                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#2563eb' }}>
                                        {d.newServiceName}
                                      </Typography>
                                      <Chip
                                        label="Nuevo servicio creado"
                                        color="primary"
                                        size='small'
                                        sx={{ mt: 0.5, fontWeight: 600, fontSize: '0.68rem', height: 22, px: 1, py: 0, minHeight: '18px', borderRadius: '8px' }}
                                      />
                                      <br />
                                      <Typography variant="caption" sx={{ color: '#666' }}>
                                        Cantidad: {d.newServiceQty} <br />
                                        Precio: {formatCurrency(d.newServicePrice)} <br />
                                        {d.newServiceDiscount > 0 && (
                                          <Typography variant="caption" sx={{ color: '#666' }}>
                                            Descuento aplicado: {formatCurrency(d.newServiceDiscount)}
                                            <br />
                                          </Typography>
                                        )}
                                        Total: {formatCurrency(d.newServicePrice * d.newServiceQty * (100 - (d.newServiceDiscount || 0)) / 100)}
                                      </Typography>
                                      <br />
                                    </Box>
                                  ) : (
                                    <Box>
                                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#2563eb' }}>
                                        Actualización de precio
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: '#666' }}>
                                        Nuevo precio: {formatCurrency(parseNumber(d.servicePrice) - parseNumber(d.amount))}
                                      </Typography>
                                      <br />
                                      <Chip
                                        label="Disminución de precio"
                                        color="primary"
                                        size="small"
                                        sx={{ mt: 1, fontWeight: 600 }}
                                      />
                                    </Box>
                                  )}
                                </TableCell>
                                {/* Monto a acreditar */}
                                <TableCell>
                                  <span style={{ color: '#2563eb', fontWeight: 700 }}>
                                    {formatCurrency(d.amount)}
                                  </span>
                                </TableCell>
                                {/* Estado */}
                                <TableCell>
                                  <Chip
                                    label={d.paymentStatus === 'pagado' ? 'Pagado' : d.paymentStatus === 'pendiente' ? 'Pendiente' : 'N/D'}
                                    color={d.paymentStatus === 'pagado' ? 'success' : d.paymentStatus === 'pendiente' ? 'warning' : 'default'}
                                    size="small"
                                    sx={{ fontWeight: 600 }}
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <Typography variant="body2" sx={{ color: '#666' }}>Sin detalles de servicios.</Typography>
                      )}
                    </Paper>
                  ))}
                </Stack>
              </Paper>
            )}
          </Container>
      ) : (
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              textAlign: 'center',
              borderRadius: 4,
              backgroundColor: 'rgba(243, 232, 255, 0.6)',
              border: '1px dashed #c4b5fd'
            }}
          >
            <ReceiptLongIcon sx={{ fontSize: 60, color: '#f759ab', mb: 2, opacity: 0.8 }} />
            <Typography variant="h5" sx={{ color: '#5b21b6', fontWeight: 600 }}>
              No se encontró información de la factura
            </Typography>
          </Paper>
        </Container>
      )}

      {/* Diálogo para registrar pagos */}
      <Dialog
        open={paymentDialogOpen}
        onClose={handleClosePaymentDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{
          backgroundColor: 'rgba(124, 58, 237, 0.05)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PaidIcon sx={{ color: '#7c3aed', mr: 1 }} />
            <Typography variant="h6" sx={{ color: '#4b0082', fontWeight: 600 }}>
              Registrar Pagos
            </Typography>
          </Box>
          <IconButton
            onClick={handleClosePaymentDialog}
            aria-label="close"
            sx={{ color: '#7c3aed' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="service-select-label">Servicios a pagar</InputLabel>
              <Select
                labelId="service-select-label"
                multiple
                value={selectedServicesForPayment}
                onChange={handleMultiServiceSelection}
                input={<OutlinedInput label="Servicios a pagar" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map(value => {
                      const detail = invoice.invoiceDetails.find(d => d.selectedServiceID === value);
                      return <Chip key={value} label={detail?.serviceName || value} size="small" />;
                    })}
                  </Box>
                )}>

                {invoice?.invoiceDetails
                  ?.filter(detail => detail.pendiente > 0.01)
                  .filter(detail => detail.originalSelectedServiceID === null)
                  .map((detail) => (
                    <MenuItem key={detail.selectedServiceID} value={detail.selectedServiceID}>
                      {detail.serviceName} - Pendiente: {formatCurrency(detail.pendiente)}
                    </MenuItem>
                  ))
                }
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: showPerServicePaymentInputs ? 1 : 2 }}>
              <TextField
                fullWidth
                label="Monto total a pagar"
                value={formatCurrency(paymentAmount)}
                disabled
                InputProps={{
                  startAdornment: <InputAdornment position="start">RD$</InputAdornment>,
                }}
              />
              {selectedServicesForPayment.length > 0 && (
                <Tooltip title={showPerServicePaymentInputs ? 'Ocultar montos por servicio' : 'Editar montos por servicio'}>
                  <IconButton color={showPerServicePaymentInputs ? 'primary' : 'default'} onClick={() => setShowPerServicePaymentInputs(v => !v)}>
                    <DriveFileRenameOutlineIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            {showPerServicePaymentInputs && selectedServicesForPayment.map(id => {
              const detail = invoice.invoiceDetails.find(d => d.selectedServiceID === id);
              if (!detail) return null;
              const currentValue = paymentServiceInputValues[id] || '';

              return (
                <Box key={id} sx={{ mb: 1.5 }}>
                  <TextField
                    fullWidth
                    label={`Monto para: ${detail.serviceName} (Pendiente ${formatCurrency(detail.pendiente)})`}
                    value={currentValue}
                    placeholder={detail.pendiente.toFixed(2)}
                    onChange={(e) => handleServicePaymentAmountChange(id, e.target.value)}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">RD$</InputAdornment>,
                    }}
                  />
                </Box>
              );
            })}

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="payment-method-label">Método de pago</InputLabel>
              <Select
                labelId="payment-method-label"
                id="payment-method"
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                label="Método de pago"
              >
                <MenuItem value="efectivo">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <MoneyIcon sx={{ mr: 1, color: 'green' }} />
                    Efectivo
                  </Box>
                </MenuItem>
                <MenuItem value="tarjeta" disabled>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CreditCardIcon sx={{ mr: 1, color: '#7c3aed' }} />
                    Tarjeta
                  </Box>
                </MenuItem>
                <MenuItem value="transferencia">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <AssuredWorkloadRounded sx={{ mr: 1, color: '#1976d2' }} />
                    Transferencia Bancaria
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
            {
              (paymentMethod !== 'efectivo' || paymentMethod === 'transferencia') && banksOptions.length > 0 && (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel id="bank-label">Banco</InputLabel>
                  <Select
                    labelId="bank-label"
                    id="bank"
                    value={bank}
                    onChange={(e) => setBank(e.target.value)}
                    label="Banco"
                  >
                    {banksOptions.map((bank) => (
                      <MenuItem key={bank.id} value={bank.id}>
                        {bank.bank}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>Seleccione el banco para la transferencia</FormHelperText>
                  <TextField
                    fullWidth
                    label="Referencia"
                    placeholder="Número de referencia de la transferencia"
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                  />
                </FormControl>
              )
            }
            <FormControl fullWidth sx={{ mb: 2 }}>
              <TextField
                labelId="description-label"
                id="description"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                multiline
                rows={4}
                variant="outlined"
                label="Notas o descripción del pago (opcional)"
                placeholder="Ingrese una nota si lo desea"
              />
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleClosePaymentDialog}
            variant="outlined"
            sx={{
              color: '#6d28d9',
              borderColor: '#6d28d9',
              '&:hover': { borderColor: '#5b21b6', backgroundColor: 'rgba(124, 58, 237, 0.04)' }
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={selectedServicesForPayment.length === 0 || paymentAmount <= 0 || processingPayment}
            onClick={handleSubmitPayment}
            sx={{
              bgcolor: '#7c3aed',
              '&:hover': { bgcolor: '#6d28d9' },
              '&.Mui-disabled': { bgcolor: 'rgba(124, 58, 237, 0.3)' }
            }}
            startIcon={<PaidIcon />}
          >
            Registrar Pago
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo para editar factura */}
      <Dialog
        open={editInvoiceDialogOpen}
        onClose={handleCloseEditInvoiceDialog}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle sx={{ color: '#4b0082', fontWeight: 700 }}>Editar Factura</DialogTitle>
        <DialogContent>
          <Table sx={{ mb: 3, minWidth: 650 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'rgba(124, 58, 237, 0.05)' }}>
                <TableCell sx={{ fontWeight: 700, color: '#4b0082' }}>Servicio</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: '#4b0082' }}>Cant.</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: '#4b0082' }}>Precio</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: '#4b0082' }}>Total</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: '#4b0082' }}>Pagado</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: '#4b0082' }}>Pendiente</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: '#4b0082' }}>Acción</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Additional services */}
              {invoice.invoiceDetails &&
                invoice.invoiceDetails
                  .filter(detail => detail.originalSelectedServiceID === null)
                  .map((detail, index) => (
                    <TableRow key={index + '-' + detail.serviceID}
                      sx={{
                        '&:last-child td, &:last-child th': { border: 0 },
                        transition: 'background 0.2s',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'rgba(124, 58, 237, 0.08)',
                        }
                      }}
                    >
                      <TableCell component="th" scope="row" sx={{ borderBottom: '1px solid rgba(124, 58, 237, 0.1)' }}>
                        <Typography variant="body2">
                          {detail.serviceName} <Chip label="Servicio" size="small" sx={{ ml: 1, fontSize: '0.7rem' }} />
                        </Typography>
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ borderBottom: '1px solid rgba(124, 58, 237, 0.1)' }}
                      >
                        {detail.serviceQty}
                      </TableCell>
                      {/* Calculate price based on servicePrice or price */}
                      <TableCell
                        align="right"
                        sx={{ borderBottom: '1px solid rgba(124, 58, 237, 0.1)' }}
                      >
                        {formatCurrency(
                          detail.servicePrice > 0
                            ? detail.servicePrice * (1 - (detail.discountApplied ? detail.discountApplied / 100 : 0))
                            : (parseNumber(detail.price)) * (1 - (detail.discountApplied ? detail.discountApplied / 100 : 0))
                        )}
                        {detail.discountApplied > 0 && (
                          <span style={{ color: '#666', fontSize: '0.8rem' }}>
                            <br />
                            {detail.discountApplied}% de descuento aplicado
                          </span>
                        )}
                        <br />
                        <span style={{ color: '#666', fontSize: '0.8rem' }}>
                          {`(P.O. ${detail.servicePrice > 0 ? formatCurrency(detail.servicePrice) : formatCurrency(parseNumber(detail.price))})`}
                        </span>
                      </TableCell>
                      {/* Calculate total amount for this service */}
                      <TableCell
                        align="right"
                        sx={{ borderBottom: '1px solid rgba(124, 58, 237, 0.1)' }}
                      >
                        {formatCurrency(
                          detail.servicePrice > 0
                            ? detail.servicePrice * parseNumber(detail.serviceQty) * (1 - (detail.discountApplied ? detail.discountApplied / 100 : 0))
                            : (parseNumber(detail.price) * parseNumber(detail.serviceQty)) * (1 - (detail.discountApplied ? detail.discountApplied / 100 : 0))
                        )}
                        {detail.discountApplied > 0 && (
                          <span style={{ color: '#666', fontSize: '0.8rem' }}>
                            <br />
                            {detail.discountApplied}% de descuento aplicado
                          </span>
                        )}
                        <br />
                        <span style={{ color: '#666', fontSize: '0.8rem' }}>
                          {`(T.O. ${detail.servicePrice > 0 ? formatCurrency(parseNumber(detail.servicePrice) * parseNumber(detail.serviceQty)) : formatCurrency(parseNumber(detail.price) * parseNumber(detail.serviceQty))})`}
                        </span>
                      </TableCell>
                      {/* Amount paid for this service */}
                      <TableCell
                        align="right"
                        sx={{ borderBottom: '1px solid rgba(124, 58, 237, 0.1)' }}
                      >
                        {formatCurrency(detail.amount)}
                      </TableCell>
                      {/* Calculate pending amount */}
                      <TableCell
                        align="right"
                        sx={{
                          borderBottom: '1px solid rgba(124, 58, 237, 0.1)',
                          color: '#d97706'
                        }}
                      >
                        {formatCurrency(detail.pendiente)}
                      </TableCell>
                      <TableCell align="right" sx={{ borderBottom: '1px solid rgba(124, 58, 237, 0.1)' }}>
                        <Tooltip title="Editar servicio">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={event => {
                              setAnchorElEdit(event.currentTarget);
                              setEditServiceIndex(index); // Guarda el índice del servicio
                            }}
                            sx={{ cursor: 'pointer' }}
                            disabled={servicesToChange.some(s => s?.serviceID === detail.serviceID) || detail.correctionType === 'debit' || detail.correctionType === 'credit'}
                          >
                            <EditIcon
                              sx={{
                                fontSize: '1.3rem',
                                color: servicesToChange.some(s => s?.serviceID === detail.serviceID) || detail.correctionType === 'debit' || detail.correctionType === 'credit' ? '#b9b6b9ff' : '#b319d2ff',
                                cursor: 'pointer'
                              }}
                            />
                          </IconButton>
                        </Tooltip>
                        <Menu
                          anchorEl={anchorElEdit}
                          open={Boolean(anchorElEdit)}
                          onClose={() => setAnchorElEdit(null)}
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                          }}
                          sx={{
                            '& .MuiPaper-root': {
                              boxShadow: '0 8px 24px rgba(35, 34, 36, 0.25)',
                            }
                          }}
                        >
                          <MenuItem
                            disabled={editServiceIndex !== null && (invoice.invoiceDetails[editServiceIndex]?.serviceID === 24 || invoice.invoiceDetails[editServiceIndex]?.serviceType === 4 || invoice.invoiceDetails[editServiceIndex]?.serviceType === 9)}
                            onClick={() => {
                              const selectedService = invoice.invoiceDetails[editServiceIndex];
                              if (selectedService?.serviceID === 24 || selectedService?.serviceID === "24") {
                                return; // No hacer nada si es servicio 24
                              }
                              if (selectedService?.serviceID === 12 || selectedService?.serviceID === "12") {
                                return; // No hacer nada si es servicio 12
                              }
                              setAnchorElEdit(null);
                              handleOpenChangeServiceDialog(selectedService, editServiceIndex);
                            }}
                            sx={{
                              opacity: (editServiceIndex !== null && (invoice.invoiceDetails[editServiceIndex]?.serviceID === 24 || invoice.invoiceDetails[editServiceIndex]?.serviceID === "24")) ? 0.5 : 1,
                              cursor: (editServiceIndex !== null && (invoice.invoiceDetails[editServiceIndex]?.serviceID === 24 || invoice.invoiceDetails[editServiceIndex]?.serviceID === "24")) ? 'not-allowed' : 'pointer'
                            }}
                          >
                            <ChangeCircleIcon
                              sx={{
                                fontSize: '1rem',
                                color: (editServiceIndex !== null && (invoice.invoiceDetails[editServiceIndex]?.serviceID === 24 || invoice.invoiceDetails[editServiceIndex]?.serviceID === "24")) ? '#cccccc' : '#b319d2ff',
                                mr: 1
                              }}
                            />
                            Cambiar servicio
                          </MenuItem>
                          <List sx={{ p: 0, minWidth: 180 }}>
                            <ListItemButton onClick={handleEditPriceClick}>
                              <ListItemIcon>
                                <BorderColorIcon sx={{ fontSize: '1rem', color: '#b319d2ff' }} />
                              </ListItemIcon>
                              <ListItemText primary="Editar precio" />
                              {openEditPrice ? <ExpandLess /> : <ExpandMore />}
                            </ListItemButton>
                            <Collapse in={openEditPrice} timeout="auto" unmountOnExit>
                              <List component="div" disablePadding>
                                <ListItemButton sx={{ pl: 4 }}
                                  onClick={() => {
                                    // Actualiza el servicio en el array y el índice actual
                                    setServicesToChange(prev => {
                                      const updated = [...prev];
                                      updated[editServiceIndex] = invoice.invoiceDetails[editServiceIndex];
                                      return updated;
                                    });
                                    setCurrentServiceIndex(editServiceIndex);
                                    handleOpenIncreasePriceDialog();
                                    setAnchorElEdit(null);
                                  }}
                                  disabled={actualNoteType === 'Crear nota de crédito'}
                                >
                                  <TrendingUpIcon sx={{ fontSize: '1rem', color: '#b319d2ff', mr: 1 }} />
                                  <ListItemText primary="Aumentar precio" />
                                </ListItemButton>
                                <ListItemButton sx={{ pl: 4 }}
                                  onClick={() => {
                                    setServicesToChange(prev => {
                                      const updated = [...prev];
                                      updated[editServiceIndex] = invoice.invoiceDetails[editServiceIndex];
                                      return updated;
                                    });
                                    setCurrentServiceIndex(editServiceIndex);
                                    handleOpenDecreasePriceDialog();
                                    setAnchorElEdit(null);
                                  }}
                                  disabled={actualNoteType === 'Crear nota de débito'}
                                >
                                  <TrendingDownIcon sx={{ fontSize: '1rem', color: '#b319d2ff', mr: 1 }} />
                                  <ListItemText primary="Disminuir precio" />
                                </ListItemButton>
                                <ListItemButton sx={{ pl: 4 }}
                                  onClick={() => {
                                    setServicesToChange(prev => {
                                      const updated = [...prev];
                                      updated[editServiceIndex] = invoice.invoiceDetails[editServiceIndex];
                                      return updated;
                                    });
                                    setCurrentServiceIndex(editServiceIndex);
                                    handleOpenDiscountDialog();
                                    setAnchorElEdit(null);
                                  }}
                                  disabled={actualNoteType === 'Crear nota de débito'}
                                >
                                  <DiscountIcon sx={{ fontSize: '1rem', color: '#2563eb', mr: 1 }} />
                                  <ListItemText primary="Aplicar descuento" />
                                </ListItemButton>
                              </List>
                            </Collapse>
                          </List>
                        </Menu>
                      </TableCell>
                    </TableRow>
                  ))
              }
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditInvoiceDialog} sx={{ color: '#d92828ff', borderColor: '#d92828ff' }} variant="outlined">
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo para cambiar servicio */}
      <Dialog
        open={changeServiceDialogOpen}
        onClose={handleCloseChangeServiceDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ color: '#4b0082', fontWeight: 700 }}>
          Cambiar Servicio
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, p: 2, border: '1px solid #b319d2ff', borderRadius: 2, backgroundColor: 'rgba(243, 232, 255, 0.1)' }}>

            <Typography variant="subtitle2" sx={{ color: '#7c3aed', fontWeight: 600, fontSize: '1.3rem', mb: 1 }}>
              Servicio actual: {servicesToChange[currentServiceIndex]?.serviceName}
            </Typography>
            <Grid container spacing={1} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <Typography variant="body2" sx={{ color: '#4b0082', fontWeight: 600 }}>
                  Precio actual: {formatCurrency(parseNumber(servicesToChange[currentServiceIndex]?.servicePrice))}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" sx={{ color: '#4b0082', fontWeight: 600 }}>
                  Cantidad actual: {parseNumber(servicesToChange[currentServiceIndex]?.serviceQty)} {servicesToChange[currentServiceIndex]?.serviceQty > 1 ? 'unidades' : 'unidad'}
                </Typography>
              </Grid>
            </Grid>
            <Grid container spacing={1} sx={{ mb: 2 }}>
              {
                servicesToChange[currentServiceIndex]?.discountApplied > 0 && (
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ color: '#4b0082', fontWeight: 600 }}>
                      Descuento aplicado: {servicesToChange[currentServiceIndex]?.discountApplied}%
                    </Typography>
                  </Grid>
                )
              }
              <Grid item xs={6}>
                <Typography variant="subtitle2" sx={{ color: '#4b0082', fontWeight: 600, fontSize: '0.9rem' }}>
                  Total facturado: {
                    formatCurrency(
                      parseNumber(servicesToChange[currentServiceIndex]?.servicePrice)
                      * parseNumber(servicesToChange[currentServiceIndex]?.serviceQty)
                      * (1 - (servicesToChange[currentServiceIndex]?.discountApplied ? servicesToChange[currentServiceIndex]?.discountApplied / 100 : 0))
                    )
                  }
                </Typography>
              </Grid>
            </Grid>
          </Box>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Buscar servicio por nombre o tipo"
            value={serviceSearch}
            onChange={handleServiceSearchChange}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1 }} />
            }}
            sx={{ mb: 2 }}
          />
          <Paper sx={{ maxHeight: 200, overflowY: 'auto', mb: 2, p: 1 }}>
            {loadingServices ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 120 }}>
                <Spin size="large" />
              </Box>
            ) : (
              <List>
                {filteredServices.map(s => (
                  <ListItemButton
                    key={s.serviceID}
                    selected={selectedNewServices[currentServiceIndex]?.serviceID === s.serviceID}
                    onClick={() => handleSelectNewService(s)}
                  >
                    <ListItemText primary={s.serviceName} secondary={`Tipo: ${s.serviceType || 'N/A'}`} />
                  </ListItemButton>
                ))}
              </List>
            )}
          </Paper>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              label="Nuevo precio"
              type="number"
              value={parseNumber(newPrices[currentServiceIndex]) || 0}
              onChange={e => handleUpdateNewPrice(currentServiceIndex, e.target.value)}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Cantidad"
              type="number"
              value={newQtys[currentServiceIndex] || ''}
              onChange={e => handleUpdateNewQty(currentServiceIndex, e.target.value)}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Descuento (%)"
              type="number"
              value={newDiscounts[currentServiceIndex] || ''}
              onChange={e => handleUpdateNewDiscount(currentServiceIndex, e.target.value)}
              inputProps={{ max: 35, min: 0 }}
              sx={{ flex: 1 }}
            />
          </Box>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Total del nuevo servicio: <b>{formatCurrency(finalNews[currentServiceIndex] || 0)}</b>
          </Typography>
          {(actualNoteType === 'Crear nota de débito' || actualNoteType === null) && noteType === 'Crear nota de débito' && (
            <Button
              variant="contained"
              color="warning"
              sx={{ mt: 2 }}
              onClick={handleOpenDebitNoteDialog}
            >
              Crear nota de débito
            </Button>
          )}
          {(actualNoteType === 'Crear nota de crédito' || actualNoteType === null) && noteType === 'Crear nota de crédito' && (
            <Button
              variant="contained"
              color="primary"
              sx={{ mt: 2 }}
              onClick={handleOpenCreditNoteDialog}
            >
              Crear nota de crédito
            </Button>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseChangeServiceDialog} variant="outlined" color="error">
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo profesional para nota de débito */}
      <Dialog
        open={debitNoteDialogOpen}
        onClose={handleCloseDebitNoteDialog}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            minHeight: '80vh', // Altura mínima
            height: '90vh',    // Altura fija si lo prefieres
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle sx={{ color: '#b319d2ff', fontWeight: 700, bgcolor: 'rgba(247, 89, 171, 0.07)' }}>
          Crear Nota de Débito
        </DialogTitle>
        <DialogContent>
          <Box sx={{
            border: '2px solid #b319d2ff',
            borderRadius: 3,
            p: 3,
            mb: 2,
            background: 'linear-gradient(90deg, #f3e8ff 60%, #fdf2f8 100%)',
            minHeight: '80%',
          }}>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <Typography variant="subtitle1" sx={{ color: '#4b0082', fontWeight: 700 }}>
                  Detalles de la Nota de Débito
                </Typography>
              </Grid>
              <Grid item xs={6} sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle1" sx={{ color: '#4b0082', fontWeight: 700 }}>
                  Factura No. {invoice?.invoiceNumber || 'N/A'}
                </Typography>
              </Grid>
            </Grid>
            <Table size="small" sx={{ mb: 2 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'rgba(179, 25, 210, 0.08)' }}>
                  <TableCell sx={{ fontWeight: 700, color: '#4b0082' }}>Servicio Original</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#4b0082' }}>Nuevo Servicio</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#4b0082' }}>Motivo</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#4b0082' }}>Monto a debitar</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#4b0082' }}>Acción</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredServicesToChange.map(({ service, newService, oldPrice, newPrice, idx }) => (
                  <TableRow key={idx}>
                    <TableCell>
                      {service?.serviceName || 'N/A'}
                      <br />
                      <span style={{ fontSize: '0.8em', color: '#666' }}>
                        {service?.serviceQty} x {formatCurrency(service?.servicePrice)}
                      </span>
                      <br />
                      {
                        service?.discountApplied > 0 && (
                          <span style={{ fontSize: '0.8em', color: '#666' }}>
                            {service?.discountApplied}% de desc. aplicado
                            <br />
                          </span>
                        )
                      }
                      <span style={{ bottom: 0 }}>
                        Total: <b>{formatCurrency(parseNumber(oldPrice) > 0 ? parseNumber(oldPrice) : (parseNumber(service.servicePrice) * (parseNumber(service.serviceQty) || 1) * (1 - (parseNumber(service.discountApplied) || 0) / 100)))}</b>
                      </span>
                    </TableCell>
                    <TableCell>
                      {newService?.serviceName || 'N/A'}
                      <br />
                      <span style={{ fontSize: '0.8em', color: '#666' }}>
                        {newQtys[idx]} x {formatCurrency(parseNumber(newService?.servicePrice) * ((100 - newDiscounts[idx]) / 100))}
                      </span>
                      <br />
                      {
                        newDiscounts[idx] > 0 && (
                          <span style={{ fontSize: '0.8em', color: '#666' }}>
                            {newDiscounts[idx]}% de desc. aplicado
                            <br />
                          </span>
                        )
                      }
                      Total: <b>{formatCurrency(finalNews[idx] || 0)}</b>
                    </TableCell>
                    <TableCell>
                      <FormControl fullWidth size="small">
                        <Select
                          value={debitNoteReasons[idx]?.option || ""}
                          onChange={e => {
                            const value = e.target.value;
                            setDebitNoteReasons(prev => {
                              const updated = [...prev];
                              updated[idx] = { option: value, custom: "" };
                              return updated;
                            });
                          }}
                          displayEmpty
                        >
                          <MenuItem value=""><em>Sin motivo</em></MenuItem>
                          {motiveDebitNote.map(opt => (
                            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {/* Si selecciona "Otro", muestra un TextField para escribir */}
                      {debitNoteReasons[idx]?.option === "Otro (especificar)" && (
                        <TextField
                          size="small"
                          fullWidth
                          variant="outlined"
                          placeholder="Escriba el motivo"
                          value={debitNoteReasons[idx]?.custom || ""}
                          onChange={e => {
                            setDebitNoteReasons(prev => {
                              const updated = [...prev];
                              updated[idx] = { ...updated[idx], custom: e.target.value };
                              return updated;
                            });
                          }}
                          sx={{ mt: 1 }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <b>{formatCurrency(newPrice - (parseNumber(oldPrice) > 0 ? parseNumber(oldPrice) : (parseNumber(service.servicePrice) * (parseNumber(service.serviceQty) || 1) * (1 - (parseNumber(service.discountApplied) || 0) / 100))))}</b>
                    </TableCell>
                    <TableCell>
                      {filteredServicesToChange.length > 1 && (
                        <Button
                          color="error"
                          variant="outlined"
                          size="small"
                          onClick={() => handleRemoveServiceFromDebitNote(idx)}
                        >
                          Eliminar
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Button
              variant="outlined"
              color="secondary"
              sx={{ mb: 2 }}
              onClick={handleAddServiceToDebitNote}
            >
              Agregar otro servicio a cambiar
            </Button>
          </Box>
          <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
            Al registrar la nota de débito, se ajustará el monto pendiente de la factura y quedará registrado en el historial.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDebitNoteDialog} variant="outlined" color="error">
            Cancelar
          </Button>
          <Button onClick={handleSubmitDebitNote} variant="contained" color="warning">
            Registrar Nota de Débito
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo profesional para nota de crédito */}
      <Dialog
        open={creditNoteDialogOpen}
        onClose={handleCloseCreditNoteDialog}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            minHeight: '80vh',
            height: '90vh',
            display: 'flex',
            flexDirection: 'column',
            background: 'linear-gradient(90deg, #fdf2f8 60%, #e0e7ff 100%)', // Colores diferentes
            border: '2px solid #2563eb'
          }
        }}
      >
        <Grid container spacing={2} sx={{ bgcolor: 'rgba(37, 99, 235, 0.07)' }}>
          <Grid item xs={10}>
            <DialogTitle sx={{ color: '#2563eb', fontWeight: 700 }}>
              Crear Nota de Crédito
            </DialogTitle>
          </Grid>
          <Grid item xs={2} sx={{ textAlign: 'right', justifyContent: 'flex-end', alignItems: 'center', display: 'flex' }}>
            <IconButton
              title="Una nota de crédito es un documento que reduce el monto pendiente de una factura, registrando devoluciones, descuentos o correcciones a favor del cliente."
              sx={{ color: '#2563eb' }}
            >
              <InfoIcon />
            </IconButton>
          </Grid>
        </Grid>
        <DialogContent>
          <Box sx={{
            border: '2px solid #2563eb',
            borderRadius: 3,
            p: 3,
            mb: 2,
            background: 'linear-gradient(90deg, #fdf2f8 60%, #e0e7ff 100%)',
            minHeight: '80%',
          }}>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <Typography variant="subtitle1" sx={{ color: '#2563eb', fontWeight: 700 }}>
                  Detalles de la Nota de Crédito
                </Typography>
              </Grid>
              <Grid item xs={6} sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle1" sx={{ color: '#2563eb', fontWeight: 700 }}>
                  Factura No. {invoice?.invoiceNumber || 'N/A'}
                </Typography>
              </Grid>
            </Grid>
            <Table size="small" sx={{ mb: 2 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'rgba(37, 99, 235, 0.08)' }}>
                  <TableCell sx={{ fontWeight: 700, color: '#2563eb' }}>Servicio Original</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#2563eb' }}>Nuevo Servicio</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#2563eb' }}>Motivo</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#2563eb' }}>Monto a acreditar</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#2563eb' }}>Acción</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredServicesToChange.map(({ service, newService, oldPrice, newPrice, idx }) => (
                  <TableRow key={idx}>
                    <TableCell>
                      {service?.serviceName || 'N/A'}
                      <br />
                      <span style={{ fontSize: '0.8em', color: '#666' }}>
                        {service?.serviceQty} x {formatCurrency(service?.servicePrice)}
                      </span>
                      <br />
                      {
                        service?.discountApplied > 0 && (
                          <span style={{ fontSize: '0.8em', color: '#666' }}>
                            {service?.discountApplied}% de desc. aplicado
                            <br />
                          </span>
                        )
                      }
                      <span style={{ bottom: 0 }}>
                        Total: <b>{formatCurrency(parseNumber(oldPrice) > 0 ? parseNumber(oldPrice) : (parseNumber(service.servicePrice) * (parseNumber(service.serviceQty) || 1) * (1 - (parseNumber(service.discountApplied) || 0) / 100)))}</b>
                      </span>
                    </TableCell>
                    <TableCell>
                      {newService?.serviceName || 'N/A'}
                      <br />
                      <span style={{ fontSize: '0.8em', color: '#666' }}>
                        {newQtys[idx]} x {formatCurrency(parseNumber(newService?.servicePrice) * ((100 - newDiscounts[idx]) / 100))}
                      </span>
                      <br />
                      {
                        newDiscounts[idx] > 0 && (
                          <span style={{ fontSize: '0.8em', color: '#666' }}>
                            {newDiscounts[idx]}% de desc. aplicado
                            <br />
                          </span>
                        )
                      }
                      Total: <b>{formatCurrency(finalNews[idx] || 0)}</b>
                    </TableCell>
                    <TableCell>
                      <FormControl fullWidth size="small">
                        <Select
                          value={debitNoteReasons[idx]?.option || ""}
                          onChange={e => {
                            const value = e.target.value;
                            setDebitNoteReasons(prev => {
                              const updated = [...prev];
                              updated[idx] = { option: value, custom: "" };
                              return updated;
                            });
                          }}
                          displayEmpty
                        >
                          <MenuItem value=""><em>Sin motivo</em></MenuItem>
                          {motiveCreditNote.map(opt => (
                            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {debitNoteReasons[idx]?.option === "Otro (especificar)" && (
                        <TextField
                          size="small"
                          fullWidth
                          variant="outlined"
                          placeholder="Escriba el motivo"
                          value={debitNoteReasons[idx]?.custom || ""}
                          onChange={e => {
                            setDebitNoteReasons(prev => {
                              const updated = [...prev];
                              updated[idx] = { ...updated[idx], custom: e.target.value };
                              return updated;
                            });
                          }}
                          sx={{ mt: 1 }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <b>- {formatCurrency((parseNumber(oldPrice) > 0 ? parseNumber(oldPrice) : (parseNumber(service.servicePrice) * (parseNumber(service.serviceQty) || 1) * (1 - (parseNumber(service.discountApplied) || 0) / 100))) - parseNumber(newPrice))}</b>
                    </TableCell>
                    <TableCell>
                      {filteredServicesToChange.length > 1 && (
                        <Button
                          color="error"
                          variant="outlined"
                          size="small"
                          onClick={() => handleRemoveServiceFromCreditNote(idx)}
                        >
                          Eliminar
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Button
              variant="outlined"
              color="primary"
              sx={{ mb: 2 }}
              onClick={handleAddServiceToCreditNote}
            >
              Agregar otro servicio a cambiar
            </Button>
          </Box>
          <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
            Al registrar la nota de crédito, se ajustará el monto pendiente de la factura y quedará registrado en el historial.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreditNoteDialog} variant="outlined" color="error">
            Cancelar
          </Button>
          <Button
            onClick={handleSubmitCreditNote}
            variant="contained"
            color="primary"
          >
            Registrar Nota de Crédito
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo para aumentar precio */}
      <Dialog
        open={increasePriceDialogOpen}
        onClose={handleCloseIncreasePriceDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ color: '#b319d2ff', fontWeight: 700 }}>
          Aumentar Precio del Servicio
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ color: '#7c3aed', fontWeight: 600, fontSize: '1.1rem', mb: 1 }}>
              Servicio seleccionado: {servicesToChange[currentServiceIndex]?.serviceName}
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
              Precio actual: <b>{formatCurrency(parseNumber(servicesToChange[currentServiceIndex]?.servicePrice))}</b>
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
              Cantidad: <b>{servicesToChange[currentServiceIndex]?.serviceQty}</b>
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
              Descuento aplicado: <b>{servicesToChange[currentServiceIndex]?.discountApplied || 0}%</b>
            </Typography>
          </Box>
          <TextField
            label="Nuevo precio"
            type="number"
            fullWidth
            value={increasePriceValue}
            onChange={e => {
              setIncreasePriceValue(e.target.value);
              setIncreasePriceError('');
            }}
            InputProps={{
              startAdornment: <InputAdornment position="start">RD$</InputAdornment>,
            }}
            error={!!increasePriceError}
            helperText={increasePriceError}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseIncreasePriceDialog} variant="outlined" color="error">
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleApplyIncreasePrice}
            disabled={!increasePriceValue || parseFloat(increasePriceValue) <= parseNumber(servicesToChange[currentServiceIndex]?.servicePrice)}
          >
            Aplicar aumento de precio
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo para disminuir precio */}
      <Dialog
        open={decreasePriceDialogOpen}
        onClose={handleCloseDecreasePriceDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ color: '#2563eb', fontWeight: 700 }}>
          Disminuir Precio del Servicio
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ color: '#2563eb', fontWeight: 600, fontSize: '1.1rem', mb: 1 }}>
              Servicio seleccionado: {servicesToChange[currentServiceIndex]?.serviceName}
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
              Precio actual: <b>{formatCurrency(parseNumber(servicesToChange[currentServiceIndex]?.servicePrice))}</b>
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
              Cantidad: <b>{servicesToChange[currentServiceIndex]?.serviceQty}</b>
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
              Descuento aplicado: <b>{servicesToChange[currentServiceIndex]?.discountApplied || 0}%</b>
            </Typography>
          </Box>
          <TextField
            label="Nuevo precio"
            type="number"
            fullWidth
            value={decreasePriceValue}
            onChange={e => {
              setDecreasePriceValue(e.target.value);
              setDecreasePriceError('');
            }}
            InputProps={{
              startAdornment: <InputAdornment position="start">RD$</InputAdornment>,
            }}
            error={!!decreasePriceError}
            helperText={decreasePriceError}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            handleCloseDecreasePriceDialog();
            setDecreasePriceValue('');
            setDecreasePriceError('');
            setEditInvoiceDialogOpen(true);
          }} variant="outlined" color="error">
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleApplyDecreasePrice}
            disabled={!decreasePriceValue || parseFloat(decreasePriceValue) >= parseNumber(servicesToChange[currentServiceIndex]?.servicePrice)}
          >
            Aplicar disminución de precio
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo para aplicar descuento */}
      <Dialog
        open={discountDialogOpen}
        onClose={handleCloseDiscountDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ color: '#2563eb', fontWeight: 700 }}>
          Aplicar Descuento al Servicio
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ color: '#2563eb', fontWeight: 600, fontSize: '1.1rem', mb: 1 }}>
              Servicio seleccionado: {selectedNewServices[currentServiceIndex]?.serviceName || servicesToChange[currentServiceIndex]?.serviceName}
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
              Precio actual: <b>{formatCurrency(parseNumber(selectedNewServices[currentServiceIndex]?.servicePrice || servicesToChange[currentServiceIndex]?.servicePrice))}</b>
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
              Cantidad: <b>{selectedNewServices[currentServiceIndex]?.serviceQty || servicesToChange[currentServiceIndex]?.serviceQty}</b>
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
              Descuento aplicado: <b>{selectedNewServices[currentServiceIndex]?.discountApplied || servicesToChange[currentServiceIndex]?.discountApplied || 0}%</b>
            </Typography>
          </Box>
          <TextField
            label="Nuevo descuento (%)"
            type="number"
            fullWidth
            value={discountValue}
            onChange={e => {
              let value = e.target.value;
              const currentDiscount = parseFloat(
                selectedNewServices[currentServiceIndex]?.discountApplied ||
                servicesToChange[currentServiceIndex]?.discountApplied ||
                0
              );

              // Evita negativos y limita a 35
              if (value === '') {
                setDiscountValue('');
                setDiscountError('');
                return;
              }
              let num = parseFloat(value);
              if (isNaN(num) || num < 0) num = 0;
              if (num > 35) num = 35;

              setDiscountValue(num.toString());

              // Validación del descuento
              if (num <= currentDiscount) {
                setDiscountError(`El descuento debe ser mayor al actual (${currentDiscount}%)`);
              } else if (num > 35) {
                setDiscountError('El descuento no puede ser mayor a 35%');
              } else {
                setDiscountError('');
              }
            }}
            slotProps={{
              input: {
                min: 0,
                max: 35,
                step: 1,
                inputMode: 'numeric',
                pattern: '[0-9]*'
              }
            }}
            error={!!discountError}
            helperText={discountError || `El descuento actual es ${selectedNewServices[currentServiceIndex]?.discountApplied || servicesToChange[currentServiceIndex]?.discountApplied || 0}%. El nuevo descuento debe ser mayor.`}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDiscountDialog} variant="outlined" color="error">
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleApplyDiscount}
            disabled={
              !discountValue ||
              parseFloat(discountValue) < 0 ||
              parseFloat(discountValue) > 35 ||
              parseFloat(discountValue) <= parseFloat(
                selectedNewServices[currentServiceIndex]?.discountApplied ||
                servicesToChange[currentServiceIndex]?.discountApplied ||
                0
              ) ||
              !!discountError
            }
          >
            Aplicar descuento
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default ViewInvoice;